import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import ReactDOM from 'react-dom';
import './ReportsConfigPage.css';
import {
  Mail, AtSign, User, Users, CheckCircle2, Pause, Clock, Calendar, CalendarCheck,
  ChevronRight, X, Plus, Trash2, AlertTriangle, Eye, Lock, Settings, Info, List,
  Navigation, Sun, TrendingUp, HardHat, ShieldAlert, Download, LucideIcon,
  Zap, HelpCircle, SquareCheck, Utensils, MessageSquare, CornerDownRight,
  Asterisk, XCircle, ChevronsUp, Search, Pencil, FileArchive, Landmark, Tag,
  ChevronDown, Check,
} from 'lucide-react';
import ConfirmModal from '../../components/ConfirmModal';
import { useSiteContext } from '../../SiteContext';
import {
  ReportsConfig, ReportConfig, ReportRecipient, RecipientType,
  ReportEmployeeResult, ReportQuestion, TimesheetQuestionOption, ReportRole,
  TimesheetAnswerType, ReportOpsManager,
} from '../../interfaces/ReportsConfiguration';
import {
  getReportsConfig, getReportsConfigErrorMessage,
  patchReport, patchReportErrorMessage,
  addRecipient, AddRecipientInput, addRecipientErrorMessage, PatchReportFlags,
  removeRecipient, removeRecipientErrorMessage,
  enrollManager, enrollManagerErrorMessage, ManagerExtras,
  patchManager, patchManagerErrorMessage,
  removeManager, removeManagerErrorMessage,
  searchReportEmployees,
  getTimesheetQuestionOptions, getReportRoles,
  addReportQuestion, addReportQuestionErrorMessage,
  patchReportQuestion, patchReportQuestionErrorMessage,
  removeReportQuestion, removeReportQuestionErrorMessage,
  downloadReportExcel, downloadReportExcelErrorMessage, ReportRunScope, ReportRunTarget,
} from '../../api/reportsConfiguration';

/** Run-panel selection: the two relative scopes plus an explicit pay period. */
type RunMode = ReportRunScope | 'period';

/**
 * Schedule text for a report that has no timer at all. Used as the marker that the
 * "set in Function App timers" note does not apply, so the header doesn't claim a
 * schedule exists where there isn't one.
 */
const ON_DEMAND_SCHEDULE = 'On demand';

// ── Per-ReportKey presentation metadata ──────────────────────────────────────
// The API returns pure data; everything display-only (icon/desc/schedule text +
// which panels apply) lives here. desc/schedule text is verbatim from the design
// prototype rpt-data.js. Unknown keys fall back to Mail + universal panels only
// (Active + Recipients), per the design README §State Management.

interface ReportMeta {
  icon: LucideIcon;
  desc: string;
  schedule: string;
  /** Ops-managers panel applies (roster / GM reports). */
  managerPanel: boolean;
  /** Panel title + drawer label ('Ops managers' | 'General managers'). */
  managerLabel: string;
  /** CcRosterSupervisors toggle applies (true roster reports only). */
  ccToggle: boolean;
  /**
   * Each enrolled manager carries its own CC list and alternate report title
   * (NPM Daily / Weekly Ops). Adds the per-row chips + inline editor and the two
   * optional inputs on the enroll popover.
   */
  managerExtras?: boolean;
  /** Primary recipients are project-derived (config CC/BCC only). */
  derivedRecipients: boolean;
  /** Read-only deadlines panel applies. */
  showDeadlines: boolean;
  /**
   * Report can be pulled on demand as a workbook (API allowlists the same keys in
   * routes/admin/reportsConfig.ts — adding it here alone does nothing).
   */
  runnable?: boolean;
  /** Trigger-questions panel applies (TAS-123). */
  questionsPanel?: boolean;
  /**
   * Nothing is ever emailed — the report only exists as an on-demand download. The
   * recipients panel and the "who will get this" affordances are hidden rather than
   * showing an empty list that reads as misconfiguration.
   */
  noRecipients?: boolean;
  /**
   * Fires on an event, not a timer. Replaces the schedule pair in the header with
   * an event pill, so an absent schedule reads as a property of the report rather
   * than a missing field.
   */
  eventDriven?: boolean;
}

const REPORT_META: Record<string, ReportMeta> = {
  DAILY_OPS: {
    icon: Sun,
    desc: 'One email per enrolled ops manager with their week-to-date crew metrics. Config CC/BCC are added to every send; when roster CC is on, that manager\'s supervisors are CC\'d too.',
    schedule: 'Mon–Fri · 5:00 AM',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: true,
    derivedRecipients: false, showDeadlines: false,
  },
  WEEKLY_OPS_EXEC: {
    icon: TrendingUp,
    desc: 'One consolidated leadership email rolling up every enrolled ops manager into a single exec summary sent to the configured TO list.',
    schedule: 'Fri · 12:00 PM',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: true,
    derivedRecipients: false, showDeadlines: false,
  },
  WEEKLY_DEADLINE: {
    icon: CalendarCheck,
    desc: 'Per-supervisor submitted / approved counts before and after the timesheet deadlines, as HTML plus an Excel attachment, to the configured recipients.',
    schedule: 'Fri · 12:00 PM',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: true,
  },
  EIGHT_HOUR_SUMMARY: {
    icon: Clock,
    desc: 'One consolidated 8-hour-workday summary to configured recipients; enrolled general managers are CC\'d on the send.',
    schedule: 'Fri · 12:00 PM',
    managerPanel: true, managerLabel: 'General managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false,
  },
  SUPER_PROJECT_WORK: {
    icon: HardHat,
    desc: 'One email per super / foreman. Primary recipients are derived from SupervisorProject tier tables, not from this config — only CC/BCC config rows apply.',
    schedule: 'Tue–Fri 5:00 AM (current wk) · Mon–Tue 5:00 AM (prior wk)',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  CREW_PROJECT_WORK: {
    icon: HardHat,
    desc: 'One email per supervisor with their crew\'s punches for the week, grouped by job and day with account code and PO #. Primary recipients are the supervisors themselves, not this config — only CC/BCC config rows apply.',
    schedule: 'Mon–Fri 5:00 AM (current week) · Mon 5:00 AM (prior week)',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  PW_EXCEPTIONS: {
    icon: ShieldAlert,
    desc: 'Employees with hours on a prevailing-wage job whose pay will export wrong — either no PW classification at all, or classified but the job has no rate for that classification. Sends Monday morning ahead of the approval deadline, and always sends: an all-clear email when there is nothing to fix.',
    schedule: 'Mon · 8:00 AM',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: true, runnable: true,
  },
  TIMESHEET_STATUS: {
    icon: SquareCheck,
    desc: 'Per-employee Submitted / Unsubmitted timesheet status for every active employee with punches in the pay period, grouped by supervisor with per-supervisor counts, as HTML plus an Excel attachment. Sends Monday morning ahead of the approval deadline, and always sends: an all-clear email when everyone has submitted.',
    schedule: 'Mon · 8:00 AM',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: true, runnable: true,
  },
  PAYROLL_TIME_OFF_OVER_EIGHT: {
    icon: AlertTriangle,
    desc: 'Employees whose approved or pending time off plus worked time exceeds 8 hours on a day, for the chosen pay period. Run on demand; nothing is emailed.',
    schedule: ON_DEMAND_SCHEDULE,
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false,
    runnable: true, noRecipients: true,
  },
  TIMESHEET_EXCEPTIONS: {
    icon: ShieldAlert,
    desc: 'Fires the moment an employee submits a timesheet, if any answer matches a configured trigger below. One email per submission — however many triggers fire, the recipients get a single message listing all of them. Every exception is also recorded on the Timesheet Exceptions page.',
    schedule: 'Runs on timesheet submission',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false,
    questionsPanel: true, eventDriven: true,
  },

  // ── WCDP (ReportEngine WCDP_* keys — ports of the old ReportApp, TAS-486) ──────────
  WCDP_DAILY_OPS: {
    icon: Sun,
    desc: 'One email per enrolled ops manager: per-supervisor punch stats (missed / manual / edited punches, exceed-5-hour lunch, overtime, gaps, exact-8-hour days) with a per-employee table per supervisor. Config CC/BCC on every send; when roster CC is on, the manager\'s supervisors are CC\'d too.',
    schedule: 'Mon 5:00 AM (prior week) · Tue–Sun 5:00 AM (current week)',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: true,
    derivedRecipients: false, showDeadlines: false,
  },
  WCDP_DAILY_OPS_PIECE: {
    icon: Sun,
    desc: 'The piece-work Daily Ops email: everything in Daily Ops plus # of tasks, supplement dollars (hours at the employee\'s rate minus pieces claimed), days without tasks and days without time, for employees who have not submitted yet.',
    schedule: 'Mon 5:00 AM (prior week) · Tue–Sun 5:00 AM (current week)',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: true,
    derivedRecipients: false, showDeadlines: false,
  },
  WCDP_WEEKLY_OPS: {
    icon: TrendingUp,
    desc: 'One email per enrolled ops manager for the closed week: supervisor summary with manual-add and edited punch counts and percentages, overtime from submitted timesheets, exceed-5-hour lunch and gap counts.',
    schedule: 'Tue · 12:00 PM',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: true,
    derivedRecipients: false, showDeadlines: false,
  },
  WCDP_WEEKLY_OPS_SUMMARY: {
    icon: TrendingUp,
    desc: 'One consolidated leadership email for the closed week, a section per enrolled ops manager with supervisors ranked by % manual punches, plus OT dollars and supplemental dollars for payroll-approved timesheets. Enrolled managers are added to the TO list.',
    schedule: 'Tue · 12:00 PM',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false,
  },
  WCDP_OPS_EXCEPTIONS: {
    icon: ShieldAlert,
    desc: 'One email per supervisor under an enrolled ops manager who has exceptions this week: exceed-5-hour meals, short meals, gaps between punches. TO is the supervisor; their ops manager and the config CC list are copied. Supervisors with nothing to fix get no email.',
    schedule: 'Mon 5:00 AM (prior week) · Tue–Sun 5:00 AM (current week)',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  WCDP_OPS_EXCEPTIONS_PIECE: {
    icon: ShieldAlert,
    desc: 'The piece-work Super Exceptions email: the same exceptions plus days with time but no tasks entered, for employees who have not submitted yet. TO is the supervisor; their ops manager and the config CC list are copied.',
    schedule: 'Mon 5:00 AM (prior week) · Tue–Sun 5:00 AM (current week)',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  WCDP_DAILY_PAYROLL: {
    icon: List,
    desc: 'Payroll\'s daily bundle: Short Meal, Exceed Five Hours, Gap and Warranty workbooks attached to one email. The on-demand download puts all four in one workbook, a sheet each.',
    schedule: 'Mon 12 PM, Tue 8 AM & 3 PM, Wed 6 AM (prior week) · Tue–Fri 8 AM (current week)',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false, runnable: true,
  },
  WCDP_DRYWALL_TASK_SUMMARY: {
    icon: HardHat,
    desc: 'One email per enrolled drywall supervisor with their crew\'s piece and misc task counts and dollars by job and class code, submitted or not. Enrolled supervisors were derived from the drywall location and can be edited here.',
    schedule: 'Mon 5:00 AM (prior week) · Tue–Sun 5:00 AM (current week)',
    managerPanel: true, managerLabel: 'Drywall supervisors', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  WCDP_DRYWALL_PIECES: {
    icon: HardHat,
    desc: 'Workbook of every submitted piece and misc task claim for the closed week, one sheet per enrolled drywall supervisor (job, account, employee, amount).',
    schedule: 'Fri · 6:00 AM',
    managerPanel: true, managerLabel: 'Drywall supervisors', ccToggle: false,
    derivedRecipients: false, showDeadlines: false, runnable: true,
  },
  WCDP_DRYWALL_MISC_TASKS: {
    icon: HardHat,
    desc: 'Workbook of drywall-location misc tasks on payroll-approved timesheets for the closed week, one sheet per ops manager (from the job\'s secondary supervisor) plus Unassigned.',
    schedule: 'Fri · 6:00 AM',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false, runnable: true,
  },
  WCDP_DRYWALL_MISC_TASKS_ALL: {
    icon: HardHat,
    desc: 'The same misc tasks workbook for every submitted timesheet, approved or not, sent early in the week while payroll is still open, with the ops managers on CC.',
    schedule: 'Mon 6:00 AM & 1:00 PM · Tue 6:00 AM',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false, runnable: true,
  },
  WCDP_PW_DAILY: {
    icon: HardHat,
    desc: 'One email per supervisor listing their employees\' hours on prevailing-wage jobs (hourly time on a project flagged prevailing wage), grouped by job with hours by day Mon–Fri. TO is the employee\'s direct supervisor; their ops manager (found through the enrolled managers below) and the config CC list are copied. Supervisors with no email fall back to the TO list here.',
    schedule: 'Mon 5:00 AM (prior week) · Tue–Sat 5:00 AM (current week)',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  WCDP_PW_WEEKLY_SUMMARY: {
    icon: HardHat,
    desc: 'Tuesday roll-up of the prior week\'s prevailing-wage hours: every supervisor, every job, hours by day, in one email to the TO list here.',
    schedule: 'Tue · 12:00 PM (prior week)',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false,
  },
  WCDP_WO_RATE_CHANGE: {
    icon: Zap,
    desc: 'Emails an ops manager whenever a Work Order hourly rate is changed for one of their supervisors\' employees, then marks the change as announced. The TO list here is the fallback when no manager can be reached through the employee\'s supervisor chain.',
    schedule: 'Every 30 minutes',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  WCDP_WO_UNSIGNED: {
    icon: Zap,
    desc: 'Friday list of work orders still waiting on a customer signature or a builder PO: each supervisor gets their own, each ops manager gets a rollup. The TO list here is the fallback for work orders with no reachable supervisor.',
    schedule: 'Fri · 8:00 AM',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  // Monthly .zip downloads — run from the PRDA Monthly Reports page (month/year picker),
  // not from here, so they are info-only on this page.
  WCDP_PRDA_MONTHLY: {
    icon: FileArchive,
    desc: 'Monthly PRDA filing built from the stored PRDA records for the reporting month, downloaded as a .zip. Run it from the PRDA Monthly Reports page under Payroll & Sync; nothing is emailed. Contains SSNs.',
    schedule: ON_DEMAND_SCHEDULE,
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false,
    runnable: false, noRecipients: true,
  },
  WCDP_PW_MONTHLY_PROJECT_HOURS: {
    icon: Landmark,
    desc: 'All hours worked on prevailing-wage projects for pay periods with check dates in the month, one file set per project, downloaded as a .zip. Run it from the PRDA Monthly Reports page under Payroll & Sync; nothing is emailed. Contains SSNs.',
    schedule: ON_DEMAND_SCHEDULE,
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false,
    runnable: false, noRecipients: true,
  },

  // ── NPM (ReportEngine NPM_* keys — ports of the NPM Function App reports) ──────
  // Schedule text is Pacific time; the engine timers are UTC (12:00 UTC = 5:00 AM PDT).
  NPM_DAILY_OPS: {
    icon: Sun,
    desc: 'One email per enrolled ops manager with week-to-date punch exceptions for their downline — missed, manual and edited punches, 5-hour meals, overtime minutes, gaps between punches and exact-8-hour days — plus an attendance section. Each manager carries their own CC list and an alternate title (e.g. "Warehouse Report") for the email, set per manager below. Config CC/BCC go on every send; when roster CC is on, the manager\'s supervisors are CC\'d too.',
    schedule: 'Mon–Wed & Fri · 5:00 AM (current week) · Thu 5:00 AM (prior week)',
    managerPanel: true, managerLabel: 'Ops Manager', ccToggle: true, managerExtras: true,
    derivedRecipients: false, showDeadlines: false,
  },
  NPM_DAILY_OPS_NV: {
    icon: Sun,
    desc: 'Nevada (Brass 2 Copper) variant of the Daily Ops Report: one email per enrolled ops manager with missed meals, total time and weekly overtime over 40 hours. CC list and alternate title are set per manager.',
    schedule: 'Mon–Wed & Fri · 5:00 AM (current week) · Thu 5:00 AM (prior week)',
    managerPanel: true, managerLabel: 'Ops Manager', ccToggle: true, managerExtras: true,
    derivedRecipients: false, showDeadlines: false,
  },
  NPM_DAILY_PIECE: {
    icon: HardHat,
    desc: 'One email per task approver with the week\'s piece-work claims. TO is the task approver from the project\'s supervisor tiers; CC is the supervisor chain plus the per-location CC setting. Those are resolved at send time — only the CC / BCC config rows here apply to every send.',
    schedule: 'Mon–Wed & Fri · 5:00 AM (current week) · Thu 5:00 AM (prior week)',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  NPM_MISC_TASK_APPROVAL: {
    icon: SquareCheck,
    desc: 'Daily misc task approval digest, one email per approver per week. TO is the approval-config head for that approver; CC comes from the approval config. Only the CC / BCC config rows here apply to every send.',
    schedule: 'Mon–Fri · 5:06 AM',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  NPM_MISC_TASK_APPROVER_REMINDER: {
    icon: Clock,
    desc: 'Reminds each misc task approver of the tasks still waiting on them. Recipients are the approvers themselves, resolved from the approval config at send time — only the CC / BCC config rows here apply.',
    schedule: 'Mon–Fri · 5:08 AM',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: false,
  },
  NPM_DAILY_PAYROLL: {
    icon: List,
    desc: 'Payroll\'s daily Gap workbook — punches with unexplained time between them for the week — attached to one email to the TO / CC list here.',
    schedule: 'Mon–Wed & Fri · 8:00 AM (current week) · Thu 8:00 AM (prior week)',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false,
  },
  NPM_WEEKLY_OPS: {
    icon: TrendingUp,
    desc: 'One email per enrolled ops manager for the closed week with their downline\'s punch exceptions and overtime, with the workbook attached. The on-demand download is the Excel: CA, NV and Company Total sheets for a pay period. CC list and alternate title are set per manager.',
    schedule: 'Mon · 12:00 PM (prior week)',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: true, managerExtras: true,
    derivedRecipients: false, showDeadlines: false, runnable: true,
  },
  NPM_WEEKLY_OPS_NV: {
    icon: TrendingUp,
    desc: 'Nevada (Brass 2 Copper) variant of the Weekly Ops Report: one email per enrolled ops manager for the closed week with missed meals, total time and weekly overtime over 40 hours. CC list and alternate title are set per manager.',
    schedule: 'Mon · 12:02 PM (prior week)',
    managerPanel: true, managerLabel: 'Ops managers', ccToggle: true, managerExtras: true,
    derivedRecipients: false, showDeadlines: false,
  },
  NPM_WEEKLY_DEADLINE: {
    icon: CalendarCheck,
    desc: 'Per-supervisor submitted / approved counts before and after the timesheet deadlines for the closed week, as HTML plus an Excel attachment, to the configured recipients. The on-demand download is the Excel for a pay period.',
    schedule: 'Mon · 12:04 PM (prior week)',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: true, runnable: true,
  },
  NPM_EIGHT_HOUR_SUMMARY: {
    icon: Clock,
    desc: 'One consolidated 8-hour-workday summary for the closed week to configured recipients; enrolled general managers are CC\'d on the send.',
    schedule: 'Mon · 12:06 PM (prior week)',
    managerPanel: true, managerLabel: 'General managers', ccToggle: false,
    derivedRecipients: false, showDeadlines: false,
  },
  NPM_APPROVER_REMINDER: {
    icon: Clock,
    desc: 'Nudges approvers with timesheets still waiting on them ahead of the approval deadline. Recipients are the approvers themselves, resolved at send time — only the CC / BCC config rows here apply.',
    schedule: 'Hourly timer; sends Mon–Wed 8/10/12/2/4 PM & Fri 10/12/2/4 PM Pacific',
    managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
    derivedRecipients: true, showDeadlines: true,
  },
};

const DEFAULT_META: ReportMeta = {
  icon: Mail, desc: '', schedule: '',
  managerPanel: false, managerLabel: 'Ops managers', ccToggle: false,
  derivedRecipients: false, showDeadlines: false, runnable: false,
};

function metaFor(reportKey: string): ReportMeta {
  return REPORT_META[reportKey] ?? DEFAULT_META;
}

const RECIPIENT_ORDER: RecipientType[] = ['TO', 'CC', 'BCC'];

/** Answer-type chip labels + icons, keyed by dbo.TimesheetQuestions.AnswerType. */
const ANSWER_TYPE_META: Record<number, { label: string; icon: LucideIcon }> = {
  [TimesheetAnswerType.YesNo]: { label: 'Yes / No', icon: HelpCircle },
  [TimesheetAnswerType.Date]: { label: 'Date', icon: Calendar },
  [TimesheetAnswerType.ConfirmCheckbox]: { label: 'Checkbox', icon: SquareCheck },
  [TimesheetAnswerType.MealConditionalWaiver]: { label: 'Meal waiver', icon: Utensils },
  [TimesheetAnswerType.Comments]: { label: 'Comments', icon: MessageSquare },
};

// ── Small helpers (initials + deterministic avatar hue — copied from GpsExceptionsPage) ──

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

function avatarColor(seed: string): string {
  let h = 7;
  for (let i = 0; i < seed.length; i++) h = (h * 33 + seed.charCodeAt(i)) & 0x7fffffff;
  return `hsl(${h % 360} 60% 42%)`;
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Jul 6 – 19, 2026" (same month) / "Jul 27 – Aug 2, 2026" / cross-year form. */
function formatPeriodRange(startStr: string, endStr: string): string {
  const s = new Date(`${startStr}T00:00:00`);
  const e = new Date(`${endStr}T00:00:00`);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return `${startStr} – ${endStr}`;
  const sM = MON[s.getMonth()], eM = MON[e.getMonth()];
  if (s.getFullYear() === e.getFullYear()) {
    if (s.getMonth() === e.getMonth()) return `${sM} ${s.getDate()} – ${e.getDate()}, ${e.getFullYear()}`;
    return `${sM} ${s.getDate()} – ${eM} ${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${sM} ${s.getDate()}, ${s.getFullYear()} – ${eM} ${e.getDate()}, ${e.getFullYear()}`;
}

/** Title-cases a rule key (SUBMISSION → "Submission") for the deadline row label. */
function deadlineLabel(ruleKey: string): string {
  return ruleKey
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

interface ResolvedDeadline {
  display: string;
  offsetText: string;
  time12: string;
  zone: string;
}

/** Client-side deadline resolution (ported from resolveDeadline() in the prototype rpt.js). */
function resolveDeadline(
  rule: { offsetDaysFromPayPeriodEnd: number; localTime: string },
  endDate: string,
  stateAbbr: string | undefined,
): ResolvedDeadline {
  const d = new Date(`${endDate}T00:00:00`);
  d.setDate(d.getDate() + rule.offsetDaysFromPayPeriodEnd);
  const [h, m] = rule.localTime.split(':').map(Number);
  d.setHours(h || 0, m || 0, 0, 0);
  const dow = d.toLocaleDateString('en-US', { weekday: 'short' });
  const md = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const tm = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const zone = stateAbbr === 'CA' ? ' PT' : '';
  const abs = Math.abs(rule.offsetDaysFromPayPeriodEnd);
  const offsetText = `${abs} day${abs === 1 ? '' : 's'} ${rule.offsetDaysFromPayPeriodEnd < 0 ? 'before' : 'after'} pay-period end`;
  return { display: `${dow} ${md}, ${tm}${zone}`, offsetText, time12: tm, zone };
}

function activeRecipientCount(r: ReportConfig): number {
  return r.recipients.length;
}

function unresolvedCount(r: ReportConfig): number {
  return r.recipients.filter((x) => x.employeeID != null && !x.resolvedEmail).length;
}

/**
 * Active but structurally unable to send anything — a roster report with no
 * enrolled managers, or an event-driven report with no trigger questions. Both
 * look configured and do nothing.
 */
function isNoop(r: ReportConfig): boolean {
  if (!r.active) return false;
  const meta = metaFor(r.reportKey);
  if (meta.managerPanel && r.opsManagers.length === 0) return true;
  if (meta.questionsPanel && r.questions.length === 0) return true;
  return false;
}

/** Selections that can never fire, because their question is off or gone. */
function deadQuestionCount(r: ReportConfig): number {
  return r.questions.filter((q) => !q.questionActive).length;
}

// ── Local toggle switch (design sizes 44×25 / 38×22) ─────────────────────────

const Toggle: React.FC<{
  on: boolean; small?: boolean; busy?: boolean; ariaLabel: string; onClick: () => void;
}> = ({ on, small, busy, ariaLabel, onClick }) => (
  <button
    type="button"
    className={`rc-switch${small ? ' sm' : ''}${on ? ' on' : ''}${busy ? ' busy' : ''}`}
    aria-label={ariaLabel}
    aria-pressed={on}
    disabled={busy}
    onClick={onClick}
  />
);

// ── Portalled popover (anchored under the Add/Enroll button) ──────────────────

const PortalPopover: React.FC<{
  anchorRect: DOMRect;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ anchorRect, onClose, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const pw = el.offsetWidth;
    const ph = el.offsetHeight;
    let left = anchorRect.right - pw;
    if (left < 12) left = 12;
    let top = anchorRect.bottom + 8;
    if (top + ph > window.innerHeight - 12) top = Math.max(12, anchorRect.top - ph - 8);
    setPos({ left, top });
  }, [anchorRect]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      // A portalled child menu (DistributionPicker) lives outside ref but belongs here.
      if (t instanceof Element && t.closest(`.${DIST_MENU_CLASS}`)) return;
      if (ref.current && !ref.current.contains(t)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    // Defer the outside-click listener so the opening click doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="rptcfg-pop open"
      style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  );
};

// ── Debounced employee search (shared by both popovers) ──────────────────────

const EmployeeSearch: React.FC<{
  usedIds: Set<number>;
  onPick: (emp: ReportEmployeeResult) => void;
}> = ({ usedIds, onPick }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ReportEmployeeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await searchReportEmployees(term);
        setResults(r);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const term = q.trim();

  return (
    <>
      <label className="fl">Search employees</label>
      <div className="empsearch">
        <input
          ref={inputRef}
          className="ti"
          placeholder="Type a name…"
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="emplist">
          {term.length < 2 ? (
            <div className="emp-none">Type at least 2 characters to search.</div>
          ) : searching ? (
            <div className="emp-none">Searching…</div>
          ) : results.length === 0 ? (
            <div className="emp-none">No employees match &ldquo;{term}&rdquo;.</div>
          ) : (
            results.map((e) => {
              const dup = usedIds.has(e.employeeID);
              return (
                <button
                  key={e.employeeID}
                  type="button"
                  className={`empopt${dup ? ' dup' : ''}`}
                  disabled={dup}
                  onClick={() => onPick(e)}
                >
                  <span className="eav" style={{ background: avatarColor(e.name) }}>{initials(e.name)}</span>
                  <span className="empmeta">
                    <span className="en">{e.name}</span>
                    <span className="ee">{e.email || 'no admin email'}</span>
                  </span>
                  {dup && <span className="dupflag">Already added</span>}
                </button>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

// ── Add-recipient popover ────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Splits a comma / semicolon / whitespace separated CC list into distinct addresses.
 * Validation is deliberately light (must contain "@") — the mail server is the
 * authority on what is deliverable, this only catches a stray name typed in by mistake.
 */
function parseCcEmails(raw: string): { emails: string[]; invalid: string[] } {
  const seen = new Set<string>();
  const emails: string[] = [];
  const invalid: string[] = [];
  raw.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean).forEach((s) => {
    if (!s.includes('@')) { invalid.push(s); return; }
    const k = s.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    emails.push(s);
  });
  return { emails, invalid };
}

// ── "Combine into email" pick-or-create combobox ─────────────────────────────
// Managers on one report that share a distribution name get ONE combined email
// (e.g. "MF HVAC" = Jim + Jorge). The picker offers the names already in use on
// this report, creates a new one from the typed text, or clears it. The menu is
// portalled + position:fixed because both hosts (.rptcfg-pop and .panel) clip
// with overflow:hidden, and so opening it never moves anything on the page.

const DIST_NAME_MAX = 100;
/** Class on the portalled menu; PortalPopover treats clicks inside it as inside. */
const DIST_MENU_CLASS = 'rc-dist-menu';

/** Trim + collapse internal whitespace — the same normalization the API applies. */
function normalizeDistName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

interface DistGroup { name: string; count: number }

/** Distinct distribution names on the report (first-seen spelling) with member counts. */
function distributionGroups(managers: ReportOpsManager[]): DistGroup[] {
  const byKey = new Map<string, DistGroup>();
  managers.forEach((m) => {
    const n = normalizeDistName(m.distributionName ?? '');
    if (!n) return;
    const k = n.toLowerCase();
    const g = byKey.get(k);
    if (g) g.count += 1;
    else byKey.set(k, { name: n, count: 1 });
  });
  return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** Typed text → the value to save: null when empty, else an existing spelling (case-insensitive) or the new name. */
function resolveDistName(raw: string, groups: DistGroup[]): string | null {
  const n = normalizeDistName(raw).slice(0, DIST_NAME_MAX);
  if (!n) return null;
  const hit = groups.find((g) => g.name.toLowerCase() === n.toLowerCase());
  return hit ? hit.name : n;
}

const managerCountLabel = (n: number) => `${n} manager${n === 1 ? '' : 's'}`;

/** A roster entry: one manager on their own, or a combined-email group of them. */
type ManagerItem =
  | { kind: 'one'; m: ReportOpsManager }
  | { kind: 'group'; key: string; name: string; members: ReportOpsManager[] };

/**
 * Clusters managers that share a distribution name at the first member's position;
 * everyone else keeps their place. Group name is the first member's spelling (the
 * API canonicalizes, so members agree anyway).
 */
function clusterByDistribution(managers: ReportOpsManager[]): ManagerItem[] {
  const groups = new Map<string, { kind: 'group'; key: string; name: string; members: ReportOpsManager[] }>();
  const items: ManagerItem[] = [];
  managers.forEach((m) => {
    const n = normalizeDistName(m.distributionName ?? '');
    if (!n) { items.push({ kind: 'one', m }); return; }
    const key = n.toLowerCase();
    const g = groups.get(key);
    if (g) { g.members.push(m); return; }
    const created = { kind: 'group' as const, key, name: n, members: [m] };
    groups.set(key, created);
    items.push(created);
  });
  return items;
}

type DistOption =
  | { kind: 'none' }
  | { kind: 'existing'; name: string; count: number }
  | { kind: 'create'; name: string };

function buildDistOptions(groups: DistGroup[], query: string): DistOption[] {
  const q = normalizeDistName(query).slice(0, DIST_NAME_MAX);
  const ql = q.toLowerCase();
  const list: DistOption[] = [{ kind: 'none' }];
  groups
    .filter((g) => !ql || g.name.toLowerCase().includes(ql))
    .forEach((g) => list.push({ kind: 'existing', name: g.name, count: g.count }));
  if (q && !groups.some((g) => g.name.toLowerCase() === ql)) list.push({ kind: 'create', name: q });
  return list;
}

const distOptionValue = (o: DistOption): string | null => (o.kind === 'none' ? null : o.name);

interface DistMenuPos { left: number; width: number; top?: number; bottom?: number; maxHeight: number }

const DistributionPicker: React.FC<{
  id: string;
  groups: DistGroup[];
  value: string | null;
  onChange: (name: string | null) => void;
  /** Keys the picker didn't consume (menu closed) — the host form's Enter = save / Escape = cancel. */
  onKeyDownPassthrough?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}> = ({ id, groups, value, onChange, onKeyDownPassthrough }) => {
  const [text, setText] = useState(value ?? '');
  const [open, setOpen] = useState(false);
  /** What filters the list; '' when opened without typing so every name shows. */
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState<DistMenuPos | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listId = `${id}-list`;

  useEffect(() => { setText(value ?? ''); }, [value]);

  const options = useMemo(() => buildDistOptions(groups, query), [groups, query]);

  const place = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    if (below < 180 && above > below) {
      setPos({ left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(260, above - 4) });
    } else {
      setPos({ left: r.left, width: r.width, top: r.bottom + 4, maxHeight: Math.min(260, Math.max(120, below - 4)) });
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  // Keep the highlighted option in view while arrowing through a long list. Only
  // the menu's own scrollTop moves — never the page.
  useEffect(() => {
    const menu = menuRef.current;
    if (!open || !menu) return;
    const el = menu.children[highlight] as HTMLElement | undefined;
    if (!el) return;
    if (el.offsetTop < menu.scrollTop) menu.scrollTop = el.offsetTop;
    else if (el.offsetTop + el.offsetHeight > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = el.offsetTop + el.offsetHeight - menu.clientHeight;
    }
  }, [open, highlight]);

  const openMenu = () => {
    if (open) return;
    const all = buildDistOptions(groups, '');
    const cur = all.findIndex((o) => distOptionValue(o) === value);
    setQuery('');
    setHighlight(cur >= 0 ? cur : 0);
    setOpen(true);
  };

  const close = () => { setOpen(false); setQuery(''); };

  const pick = (o: DistOption) => {
    const next = distOptionValue(o);
    setText(next ?? '');
    close();
    if (next !== value) onChange(next);
  };

  /** Blur (Tab / click elsewhere / Save) keeps what was typed, resolved like a pick. */
  const commit = () => {
    const next = resolveDistName(text, groups);
    setText(next ?? '');
    close();
    if (next !== value) onChange(next);
  };

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = e.target.value;
    setText(t);
    setQuery(t);
    // Typing lands on the first real match (or Create), not on "Send separately".
    const next = buildDistOptions(groups, t);
    setHighlight(normalizeDistName(t) && next.length > 1 ? 1 : 0);
    setOpen(true);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (!open) { openMenu(); return; }
      const n = options.length;
      setHighlight((h) => (e.key === 'ArrowDown' ? Math.min(h + 1, n - 1) : Math.max(h - 1, 0)));
      return;
    }
    if (open && e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const o = options[Math.min(highlight, options.length - 1)];
      if (o) pick(o);
      return;
    }
    if (open && e.key === 'Escape') {
      // Only the menu closes — not the enroll popover / inline editor around it.
      e.preventDefault();
      e.stopPropagation();
      setText(value ?? '');
      close();
      return;
    }
    onKeyDownPassthrough?.(e);
  };

  const hl = Math.min(highlight, options.length - 1);

  return (
    <div className="rc-dist">
      <input
        ref={inputRef}
        id={id}
        className="ti"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open ? `${id}-opt-${hl}` : undefined}
        placeholder="Sends its own email"
        autoComplete="off"
        spellCheck={false}
        maxLength={DIST_NAME_MAX}
        value={text}
        onChange={onInput}
        onClick={openMenu}
        onFocus={(e) => e.target.select()}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
      <ChevronDown size={15} className="rc-dist-chev" aria-hidden />
      {open && ReactDOM.createPortal(
        <div
          ref={menuRef}
          id={listId}
          role="listbox"
          aria-label="Combine into email"
          className={DIST_MENU_CLASS}
          style={pos
            ? { left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }
            : { visibility: 'hidden' }}
          // Keep focus in the input (no blur-commit) and keep the enroll popover open.
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          {options.map((o, i) => {
            const selected = o.kind !== 'create' && distOptionValue(o) === value;
            return (
              <div
                key={o.kind === 'none' ? '__none' : `${o.kind}:${o.name}`}
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={selected}
                className={`rdo ${o.kind}${i === hl ? ' hl' : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(o)}
              >
                {o.kind === 'none' && (
                  <><Mail size={14} className="rdo-ic" /><span className="rdo-name">Send separately</span><span className="rdo-sub">own email</span></>
                )}
                {o.kind === 'existing' && (
                  <><Users size={14} className="rdo-ic" /><span className="rdo-name">{o.name}</span><span className="rdo-sub">· {managerCountLabel(o.count)}</span></>
                )}
                {o.kind === 'create' && (
                  <><Plus size={14} className="rdo-ic" /><span className="rdo-name">Create &ldquo;{o.name}&rdquo;</span></>
                )}
                <Check size={14} className={`rdo-check${selected ? ' on' : ''}`} aria-hidden />
              </div>
            );
          })}
          {groups.length === 0 && !normalizeDistName(query) && (
            <div className="rdo-empty">No combined emails on this report yet — type a name to create one.</div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

const AddRecipientPopover: React.FC<{
  report: ReportConfig;
  anchorRect: DOMRect;
  onClose: () => void;
  onSubmit: (input: AddRecipientInput) => Promise<void>;
}> = ({ report, anchorRect, onClose, onSubmit }) => {
  const [type, setType] = useState<RecipientType>('TO');
  const [src, setSrc] = useState<'email' | 'emp'>('emp');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [bad, setBad] = useState(false);

  const usedIds = useMemo(
    () => new Set(report.recipients.filter((x) => x.employeeID != null && x.recipientType === type).map((x) => x.employeeID as number)),
    [report.recipients, type],
  );

  const addLiteral = async () => {
    const v = email.trim();
    if (!EMAIL_RE.test(v)) { setBad(true); setErr('Enter a valid email address.'); return; }
    if (report.recipients.some((x) => (x.email || '').toLowerCase() === v.toLowerCase() && x.recipientType === type)) {
      setBad(true); setErr(`${v} is already a ${type} recipient.`); return;
    }
    try {
      await onSubmit({ recipientType: type, email: v });
      onClose();
    } catch (e) {
      setBad(true); setErr(addRecipientErrorMessage(e));
    }
  };

  const addEmployee = async (emp: ReportEmployeeResult) => {
    try {
      await onSubmit({ recipientType: type, employeeID: emp.employeeID });
      onClose();
    } catch (e) {
      setErr(addRecipientErrorMessage(e));
    }
  };

  return (
    <PortalPopover anchorRect={anchorRect} onClose={onClose}>
      <div className="pp-head">
        <h4>Add recipient</h4>
        <button type="button" className="x" onClick={onClose}><X size={15} /></button>
      </div>
      <div className="pp-body">
        <div className="seg">
          {RECIPIENT_ORDER.map((t) => (
            <button key={t} type="button" className={t === type ? 'on' : ''} onClick={() => { setType(t); setErr(null); setBad(false); }}>{t}</button>
          ))}
        </div>
        <div className="src">
          <button type="button" className={src === 'emp' ? 'on' : ''} onClick={() => { setSrc('emp'); setErr(null); setBad(false); }}>
            <User size={14} />Employee
          </button>
          <button type="button" className={src === 'email' ? 'on' : ''} onClick={() => { setSrc('email'); setErr(null); setBad(false); }}>
            <AtSign size={14} />Email address
          </button>
        </div>

        {src === 'email' ? (
          <>
            <label className="fl">Email address</label>
            <input
              className={`ti${bad ? ' bad' : ''}`}
              placeholder="name@company.com"
              autoComplete="off"
              autoFocus
              value={email}
              onChange={(e) => { setEmail(e.target.value); setBad(false); setErr(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') addLiteral(); }}
            />
          </>
        ) : (
          <EmployeeSearch usedIds={usedIds} onPick={addEmployee} />
        )}

        {err && (
          <div className="pp-err show"><AlertTriangle size={14} /><span>{err}</span></div>
        )}

        {src === 'email' && (
          <div className="pp-foot">
            <button type="button" className="btn sm" onClick={onClose}>Cancel</button>
            <button type="button" className="btn sm primary" onClick={addLiteral}><Plus size={15} />Add</button>
          </div>
        )}
      </div>
    </PortalPopover>
  );
};

// ── Enroll-manager popover ───────────────────────────────────────────────────

const EnrollManagerPopover: React.FC<{
  report: ReportConfig;
  managerLabel: string;
  /** Two-step flow: pick the employee, then the optional CC list / alternate title / display name. */
  managerExtras?: boolean;
  anchorRect: DOMRect;
  onClose: () => void;
  onSubmit: (employeeID: number, extras?: ManagerExtras) => Promise<void>;
}> = ({ report, managerLabel, managerExtras, anchorRect, onClose, onSubmit }) => {
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<ReportEmployeeResult | null>(null);
  const [cc, setCc] = useState('');
  const [title, setTitle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [distributionName, setDistributionName] = useState<string | null>(null);
  const [bad, setBad] = useState(false);
  const [saving, setSaving] = useState(false);
  const usedIds = useMemo(
    () => new Set(report.opsManagers.map((m) => m.employeeID)),
    [report.opsManagers],
  );
  const distGroups = useMemo(() => distributionGroups(report.opsManagers), [report.opsManagers]);

  const pick = async (emp: ReportEmployeeResult) => {
    if (usedIds.has(emp.employeeID)) { setErr(`${emp.name} is already enrolled.`); return; }
    if (managerExtras) { setPicked(emp); setErr(null); return; }
    try {
      await onSubmit(emp.employeeID);
      onClose();
    } catch (e) {
      setErr(enrollManagerErrorMessage(e));
    }
  };

  const enrollPicked = async () => {
    if (!picked || saving) return;
    const { emails, invalid } = parseCcEmails(cc);
    if (invalid.length) {
      setBad(true);
      setErr(`Not an email address: ${invalid.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(picked.employeeID, {
        ccEmails: emails,
        alternateTitle: title.trim() || null,
        displayName: displayName.trim() || null,
        // Only sent when set, so enrolling without it never depends on the new column.
        ...(distributionName ? { distributionName } : {}),
      });
      onClose();
    } catch (e) {
      setErr(enrollManagerErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalPopover anchorRect={anchorRect} onClose={onClose}>
      <div className="pp-head">
        <h4>Enroll {managerLabel.toLowerCase()}</h4>
        <button type="button" className="x" onClick={onClose}><X size={15} /></button>
      </div>
      <div className="pp-body">
        {!picked ? (
          <EmployeeSearch usedIds={usedIds} onPick={pick} />
        ) : (
          <>
            <div className="qpicked mpicked">
              <span className="eav" style={{ background: avatarColor(picked.name) }}>{initials(picked.name)}</span>
              <span className="empmeta">
                <span className="en">{picked.name}</span>
                <span className="ee">{picked.email || 'no admin email'}</span>
              </span>
              <button type="button" className="qpicked-back" onClick={() => { setPicked(null); setErr(null); setBad(false); }}>Change</button>
            </div>

            <label className="fl" htmlFor="enroll-cc">CC emails <span className="opt">optional</span></label>
            <input
              id="enroll-cc"
              className={`ti${bad ? ' bad' : ''}`}
              placeholder="name@company.com, other@company.com"
              autoComplete="off"
              autoFocus
              value={cc}
              onChange={(e) => { setCc(e.target.value); setBad(false); setErr(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') enrollPicked(); }}
            />
            <div className="qpop-note">Copied on this manager&apos;s email only. Separate addresses with commas.</div>

            <label className="fl mt" htmlFor="enroll-title">Alternate title <span className="opt">optional</span></label>
            <input
              id="enroll-title"
              className="ti"
              placeholder='e.g. "Warehouse Report"'
              autoComplete="off"
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') enrollPicked(); }}
            />
            <div className="qpop-note">Replaces the report title in this manager&apos;s email. Leave blank for the default.</div>

            <label className="fl mt" htmlFor="enroll-display-name">Display name <span className="opt">optional</span></label>
            <input
              id="enroll-display-name"
              className="ti"
              placeholder={picked.name}
              autoComplete="off"
              maxLength={200}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') enrollPicked(); }}
            />
            <div className="qpop-note">Shown in the email subject and heading; blank uses the employee record.</div>

            <label className="fl mt" htmlFor="enroll-dist">Combine into email <span className="opt">optional</span></label>
            <DistributionPicker
              id="enroll-dist"
              groups={distGroups}
              value={distributionName}
              onChange={setDistributionName}
              onKeyDownPassthrough={(e) => { if (e.key === 'Enter') enrollPicked(); }}
            />
            <div className="qpop-note">Managers with the same name here share one email. Leave empty to send this manager their own.</div>
          </>
        )}
        {err && (
          <div className="pp-err show"><AlertTriangle size={14} /><span>{err}</span></div>
        )}
        {picked && (
          <div className="pp-foot">
            <button type="button" className="btn sm" onClick={onClose}>Cancel</button>
            <button type="button" className="btn sm primary" disabled={saving} onClick={enrollPicked}>
              <Plus size={15} />{saving ? 'Enrolling…' : 'Enroll'}
            </button>
          </div>
        )}
      </div>
    </PortalPopover>
  );
};

// ── Resolved "Who will get this?" drawer ─────────────────────────────────────

const ResolvedDrawer: React.FC<{
  report: ReportConfig;
  config: ReportsConfig;
  onClose: () => void;
}> = ({ report, config, onClose }) => {
  const meta = metaFor(report.reportKey);
  const period = config.payPeriod;
  const stateAbbr = config.settings.StateAbbr;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const byType = (t: RecipientType) => report.recipients.filter((x) => x.recipientType === t);

  const emailLine = (x: ReportRecipient) => {
    if (x.employeeID != null && !x.resolvedEmail) {
      return (
        <div key={x.rptReportRecipientID} className="em-line warn">
          <AlertTriangle size={14} />{x.employeeName}<span className="via">unresolved — skipped</span>
        </div>
      );
    }
    const via = x.employeeID != null ? `via ${x.employeeName}` : 'literal';
    return (
      <div key={x.rptReportRecipientID} className="em-line">
        <Mail size={14} />{x.resolvedEmail}<span className="via">{via}</span>
      </div>
    );
  };

  const section = (t: RecipientType) => {
    const all = byType(t);
    if (!all.length) return null;
    const sending = all.filter((x) => !(x.employeeID != null && !x.resolvedEmail)).length;
    const long = t === 'TO' ? 'To' : t === 'CC' ? 'Carbon copy' : 'Blind copy';
    return (
      <div className="dr-sec" key={t}>
        <div className="ds-lab">
          <span className={`rtype ${t}`}>{t}</span>{long}
          <span className="ds-count">{sending} sending</span>
        </div>
        {all.map(emailLine)}
      </div>
    );
  };

  const noopBanner = meta.managerPanel && report.active && report.opsManagers.length === 0
    ? (
      <div className="rc-banner warn dr-banner">
        <AlertTriangle size={19} />
        <div><b>This report will send nothing.</b> No managers are enrolled.</div>
      </div>
    )
    : !report.active
      ? (
        <div className="rc-banner warn dr-banner">
          <Pause size={19} />
          <div><b>Report is paused.</b> The resolved output below is what <i>would</i> send once reactivated.</div>
        </div>
      )
      : null;

  return ReactDOM.createPortal(
    <>
      <div className="rptcfg-drawer-bg open" onClick={onClose} />
      <div className="rptcfg-drawer open" role="dialog" aria-label="Who will get this?">
        <div className="dr-head">
          <span className="di"><Eye size={22} /></span>
          <div>
            <h3>Who will get this?</h3>
            <div className="dr-sub">{report.name} · resolved output</div>
          </div>
          <button type="button" className="x" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="dr-period">
          <span className="pp-lab">Pay period</span>
          <span className="dr-badge">W{period.weekNumber}</span>
          {formatPeriodRange(period.startDate, period.endDate)}
          <span className="dr-mirror"><Info size={14} /> mirrors engine ConfigCheck</span>
        </div>
        <div className="dr-body">
          {noopBanner}

          {meta.derivedRecipients && (
            <div className="dr-sec">
              <div className="ds-lab"><Navigation size={14} />Project-derived primary recipients</div>
              <div className="em-line"><User size={14} />One super / foreman per active project<span className="via">from SupervisorProject</span></div>
              <div className="em-empty">Resolved from tier tables at send time — not shown here.</div>
            </div>
          )}

          {section('TO')}
          {section('CC')}
          {section('BCC')}

          {meta.managerPanel && (
            report.opsManagers.length === 0 ? (
              <div className="dr-sec">
                <div className="ds-lab"><Users size={14} />Rosters<span className="ds-count">0 managers</span></div>
                <div className="em-line warn"><AlertTriangle size={14} />No managers enrolled — report sends nothing</div>
              </div>
            ) : (
              <div className="dr-sec">
                <div className="ds-lab"><Users size={14} />{meta.managerLabel} rosters<span className="ds-count">{report.opsManagers.length}</span></div>
                {report.opsManagers.map((m) => (
                  <div className="ros" key={m.rptReportOpsManagerID}>
                    <div className="ros-head">
                      <span className="ros-av" style={{ background: avatarColor(m.name) }}>{initials(m.name)}</span>
                      <span className="ros-nm">{m.name}</span>
                      <span className="ros-sum"><b>{m.supervisorCount}</b> sups · <b>{m.downlineEmployeeCount}</b> downline</span>
                    </div>
                    {meta.managerExtras && (m.displayName || m.alternateTitle || m.distributionName || (m.ccEmails ?? []).length > 0) && (
                      <div className="ros-extra">
                        {m.distributionName && <span className="rx-dist"><Mail size={12} />Combined into &ldquo;{m.distributionName}&rdquo;</span>}
                        {m.displayName && <span className="rx-name"><User size={12} />Addressed as &ldquo;{m.displayName}&rdquo;</span>}
                        {m.alternateTitle && <span className="rx-title"><Tag size={12} />Sent as &ldquo;{m.alternateTitle}&rdquo;</span>}
                        {(m.ccEmails ?? []).length > 0 && (
                          <span className="rx-cc"><AtSign size={12} />CC {(m.ccEmails ?? []).join(', ')}</span>
                        )}
                      </div>
                    )}
                    <div className="ros-sups">
                      {m.supervisors.length === 0 ? (
                        <div className="em-empty">Roster resolves at send time.</div>
                      ) : (
                        m.supervisors.map((s) => (
                          <div className="sup-line" key={s.employeeID}>
                            <span className="sn">{s.name}</span>
                            <span className="se">{s.email || 'no email'}</span>
                            <span className="sd">{s.directReports} reports</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {meta.showDeadlines && config.deadlineRules.length > 0 && (
            <div className="dr-sec">
              <div className="ds-lab"><Calendar size={14} />Resolved deadlines · W{period.weekNumber}</div>
              {config.deadlineRules.map((rule) => {
                const res = resolveDeadline(rule, period.endDate, stateAbbr);
                return (
                  <div className="dl-line" key={rule.ruleKey}>
                    <span className="dk">{rule.ruleKey}</span>{deadlineLabel(rule.ruleKey)}
                    <span className="di2">{res.display}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

// ── Main page ────────────────────────────────────────────────────────────────

/**
 * Two-step: pick a question, then choose what answer fires it. A popover, not a
 * modal — the same primitive as AddRecipientPopover, just wider, because question
 * text is long.
 */
const AddQuestionPopover: React.FC<{
  report: ReportConfig;
  options: TimesheetQuestionOption[];
  anchorRect: DOMRect;
  onClose: () => void;
  onSubmit: (input: { timesheetQuestionsID: number; triggerAnswer: string | null }) => Promise<void>;
}> = ({ report, options, anchorRect, onClose, onSubmit }) => {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<TimesheetQuestionOption | null>(null);
  const [trigger, setTrigger] = useState<string | null>('No');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const alreadyAdded = useMemo(
    () => new Set(report.questions.map((q) => q.timesheetQuestionsID)),
    [report.questions],
  );

  // Flat list with children indented, so an inactive question or a child whose
  // parent is missing stays visible instead of vanishing inside a tree.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (o: TimesheetQuestionOption) => !q
      || o.question.toLowerCase().includes(q)
      || (o.questionEs ?? '').toLowerCase().includes(q);

    const roots = options.filter((o) => o.parentQuestionID == null);
    const out: { opt: TimesheetQuestionOption; child: boolean }[] = [];
    for (const root of roots) {
      const kids = options.filter((o) => o.parentQuestionID === root.timesheetQuestionsID);
      const rootMatch = matches(root);
      const kidMatches = kids.filter(matches);
      if (rootMatch || kidMatches.length) out.push({ opt: root, child: false });
      for (const k of (rootMatch ? kids : kidMatches)) out.push({ opt: k, child: true });
    }
    // Orphans — a child whose parent row is gone. Never silently dropped.
    for (const o of options) {
      if (o.parentQuestionID != null
        && !options.some((p) => p.timesheetQuestionsID === o.parentQuestionID)
        && matches(o)) {
        out.push({ opt: o, child: false });
      }
    }
    return out;
  }, [options, query]);

  const canValue = picked != null && picked.suggestedTriggerValues.length > 0;
  const parentOf = picked?.parentQuestionID != null
    ? options.find((o) => o.timesheetQuestionsID === picked.parentQuestionID) ?? null
    : null;
  const hasKids = picked != null && options.some((o) => o.parentQuestionID === picked.timesheetQuestionsID);

  const pick = (opt: TimesheetQuestionOption) => {
    setPicked(opt);
    // Date and Comments can only ever be "any answer".
    setTrigger(opt.suggestedTriggerValues.length > 0 ? 'No' : null);
  };

  const submit = async () => {
    if (!picked || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit({ timesheetQuestionsID: picked.timesheetQuestionsID, triggerAnswer: canValue ? trigger : null });
      onClose();
    } catch (e) {
      setError(addReportQuestionErrorMessage(e));
      setSaving(false);
    }
  };

  return (
    <PortalPopover anchorRect={anchorRect} onClose={onClose}>
      <div className="pp-head">
        <h4>{picked ? 'Fires when the answer is' : 'Add a trigger question'}</h4>
        <button type="button" className="x" onClick={onClose}><X size={15} /></button>
      </div>

      {!picked ? (
        <div className="pp-body qpick">
          <div className="qsearch">
            <Search size={14} />
            <input
              className="ti"
              autoFocus
              autoComplete="off"
              value={query}
              placeholder="Search questions…"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="qoptlist">
            {visible.length === 0 && <div className="emp-none">No questions match.</div>}
            {visible.map(({ opt, child }) => {
              const added = alreadyAdded.has(opt.timesheetQuestionsID);
              return (
                <button
                  type="button"
                  key={opt.timesheetQuestionsID}
                  className={`qopt${child ? ' child' : ''}${added ? ' dup' : ''}${opt.active ? '' : ' off'}`}
                  disabled={added}
                  onClick={() => pick(opt)}
                >
                  <span className="qopt-t">{opt.question}</span>
                  <span className="qopt-meta">
                    {ANSWER_TYPE_META[opt.answerType]?.label ?? 'Answer'}
                    {!opt.active && <span className="qopt-off"><Pause size={10} />Switched off</span>}
                    {added && <span className="dupflag">Already added</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="pp-body">
          <div className="qpicked">
            <div className="qpicked-t">{picked.question}</div>
            <button type="button" className="qpicked-back" onClick={() => setPicked(null)}>Pick a different question</button>
          </div>

          {canValue ? (
            <>
              <div className="tseg full">
                {(['No', 'Yes', null] as (string | null)[]).map((v) => (
                  <button
                    key={v ?? 'any'}
                    type="button"
                    className={`tseg-b${trigger === v ? ' on' : ''}${v === 'No' ? ' no' : ''}${v === 'Yes' ? ' yes' : ''}`}
                    onClick={() => setTrigger(v)}
                  >
                    {v ?? 'Any answer'}
                  </button>
                ))}
              </div>
              <div className="qbilingual">Matches both English and Spanish answers (Yes / Sí).</div>
            </>
          ) : (
            <div className="qpop-note">
              {picked.answerType === TimesheetAnswerType.Comments ? (
                <><b>Comments fire on any text the employee enters.</b> There is nothing to match against, so no trigger value is needed.</>
              ) : (
                <><b>Date answers fire on any date entered.</b> Exact-matching a date isn&apos;t meaningful, so no trigger value is needed.</>
              )}
            </div>
          )}

          {parentOf && (
            <div className="qpop-note">
              Only asked when &ldquo;{parentOf.question}&rdquo; is answered <b>Yes</b>. Its answer is <b>already included
              automatically</b> whenever that question fires — select it here only if you also want this answer to raise
              an exception of its own.
            </div>
          )}
          {hasKids && (
            <div className="qpop-note">
              <b>Child answers come along automatically.</b> When this question fires, the answers to the questions it
              unlocks are captured with it — you don&apos;t need to select them.
            </div>
          )}
          {!picked.active && (
            <div className="qpop-note amber">
              <b>This question is switched off.</b> You can select it, but it can&apos;t fire until someone turns it back on.
            </div>
          )}

          {error && (
            <div className="pp-err show"><AlertTriangle size={14} /><span>{error}</span></div>
          )}
          <div className="pp-foot">
            <button type="button" className="btn sm" onClick={onClose}>Cancel</button>
            <button type="button" className="btn sm primary" disabled={saving} onClick={submit}>
              <Plus size={15} />Add question
            </button>
          </div>
        </div>
      )}
    </PortalPopover>
  );
};

type PopoverState =
  | { kind: 'recipient'; reportId: number; anchorRect: DOMRect }
  | { kind: 'manager'; reportId: number; anchorRect: DOMRect }
  | { kind: 'question'; reportId: number; anchorRect: DOMRect }
  | null;

type RemoveConfirm =
  | { kind: 'recipient'; id: number }
  | { kind: 'manager'; id: number }
  | { kind: 'question'; id: number }
  | null;

const ReportsConfigPage: React.FC = () => {
  const { createPopup } = useSiteContext();

  const [config, setConfig] = useState<ReportsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

  const [busy, setBusy] = useState(false);
  /** Which control is mid-save, e.g. "question:12" — drives the inline spinner. */
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [popover, setPopover] = useState<PopoverState>(null);
  const [removeConfirm, setRemoveConfirm] = useState<RemoveConfirm>(null);
  const [pauseConfirmId, setPauseConfirmId] = useState<number | null>(null);
  const [drawerReportId, setDrawerReportId] = useState<number | null>(null);

  const [runMode, setRunMode] = useState<RunMode>('prior');
  const [runPayPeriodID, setRunPayPeriodID] = useState<number | null>(null);
  const [running, setRunning] = useState(false);

  // Trigger questions (TAS-123). Both lists are small and only used by the
  // questions panel; they load alongside the config and failure is tolerated so a
  // missing endpoint degrades the panel rather than breaking the page.
  const [questionOptions, setQuestionOptions] = useState<TimesheetQuestionOption[]>([]);
  const [roles, setRoles] = useState<ReportRole[]>([]);
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
  /** The value being considered while editing — committed only on Save. */
  const [pendingTrigger, setPendingTrigger] = useState<string | null>(null);
  const [esOpen, setEsOpen] = useState<Set<number>>(new Set());

  // Per-manager extras (CC list + alternate title + display name) inline editor. One
  // row edits at a time; drafts are committed only on Save.
  const [editingManagerId, setEditingManagerId] = useState<number | null>(null);
  const [mgrCcDraft, setMgrCcDraft] = useState('');
  const [mgrTitleDraft, setMgrTitleDraft] = useState('');
  const [mgrNameDraft, setMgrNameDraft] = useState('');
  const [mgrDistDraft, setMgrDistDraft] = useState<string | null>(null);
  const [mgrEditErr, setMgrEditErr] = useState<string | null>(null);

  /** Pay periods offered by the run panel's "Pick a period" option, newest first. */
  const recentPeriods = useMemo(
    () => [...(config?.recentPayPeriods ?? [])].sort((a, b) => b.payPeriodID - a.payPeriodID),
    [config],
  );

  // Default to the most recent *closed* period: the one payroll is reconciling. The
  // period in progress is still filling in, and is already reachable as "This week".
  useEffect(() => {
    if (runPayPeriodID != null || recentPeriods.length === 0) return;
    const currentID = config?.payPeriod?.payPeriodID;
    const closed = recentPeriods.find((p) => p.payPeriodID !== currentID) ?? recentPeriods[0];
    setRunPayPeriodID(closed.payPeriodID);
  }, [recentPeriods, runPayPeriodID, config]);

  const beginEdit = (q: ReportQuestion) => {
    setPendingTrigger(q.triggerAnswer);
    setEditingQuestionId(q.rptReportQuestionID);
  };
  const cancelEdit = () => {
    setEditingQuestionId(null);
    setPendingTrigger(null);
  };

  const toggleEs = (id: number) => setEsOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /** Parent question text for a selected child whose parent is NOT selected. */
  const parentLabel = (q: ReportQuestion): string => {
    const opt = questionOptions.find((o) => o.timesheetQuestionsID === q.parentQuestionID);
    return opt?.question ?? `question ${q.parentQuestionID}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getReportsConfig();
      setConfig(data);

      // Best effort: the questions panel degrades without these, the page does not.
      const [opts, roleList] = await Promise.allSettled([
        getTimesheetQuestionOptions(true),
        getReportRoles(),
      ]);
      if (opts.status === 'fulfilled') setQuestionOptions(opts.value);
      if (roleList.status === 'fulfilled') setRoles(roleList.value);
    } catch (e) {
      setLoadError(getReportsConfigErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Refetch the whole config after a successful mutation (payload is small). */
  const reload = useCallback(async () => {
    const data = await getReportsConfig();
    setConfig(data);
  }, []);

  /** Wraps a mutation: runs it, refetches, and surfaces failures via createPopup. */
  const runMutation = useCallback(async (fn: () => Promise<void>, errFn: (e: unknown) => string, key?: string) => {
    setBusy(true);
    if (key) setSavingKey(key);
    try {
      await fn();
      await reload();
    } catch (e) {
      createPopup('Couldn\'t save changes', errFn(e));
    } finally {
      setBusy(false);
      setSavingKey(null);
    }
  }, [reload, createPopup]);

  /**
   * A save that applies the server's own response to local state instead of
   * refetching the whole config. Used for the in-place edits (trigger value, the
   * chain flags) where the PATCH returns the authoritative updated row — a full
   * GET /config re-resolves every ops-manager roster, which is what made these
   * feel slow.
   */
  const runSave = useCallback(async (key: string, fn: () => Promise<void>, errFn: (e: unknown) => string) => {
    setBusy(true);
    setSavingKey(key);
    try {
      await fn();
    } catch (e) {
      createPopup('Couldn\'t save changes', errFn(e));
      // The local state may now disagree with the server, so resync.
      try { await reload(); } catch { /* the popup already told them */ }
    } finally {
      setBusy(false);
      setSavingKey(null);
    }
  }, [createPopup, reload]);

  /** Splices one updated question into local state, in place. */
  const applyQuestion = useCallback((updated: ReportQuestion) => {
    setConfig((prev) => (prev ? {
      ...prev,
      reports: prev.reports.map((r) => ({
        ...r,
        questions: r.questions.map((q) => (
          q.rptReportQuestionID === updated.rptReportQuestionID ? updated : q
        )),
      })),
    } : prev));
  }, []);

  /** Splices one updated ops manager (with its roster) into local state, in place. */
  const applyManager = useCallback((updated: ReportOpsManager) => {
    setConfig((prev) => (prev ? {
      ...prev,
      reports: prev.reports.map((r) => ({
        ...r,
        opsManagers: r.opsManagers.map((m) => (
          m.rptReportOpsManagerID === updated.rptReportOpsManagerID ? updated : m
        )),
      })),
    } : prev));
  }, []);

  /** Applies report-level flags to local state, in place. */
  const applyReportFlags = useCallback((rptReportID: number, flags: PatchReportFlags) => {
    setConfig((prev) => (prev ? {
      ...prev,
      reports: prev.reports.map((r) => (r.rptReportID === rptReportID ? { ...r, ...flags } : r)),
    } : prev));
  }, []);

  const openReport = (id: number) => {
    setSelectedReportId(id);
    setView('detail');
    window.scrollTo(0, 0);
  };
  const goList = () => { setView('list'); };

  const report = useMemo(
    () => (config && selectedReportId != null ? config.reports.find((r) => r.rptReportID === selectedReportId) ?? null : null),
    [config, selectedReportId],
  );

  const drawerReport = useMemo(
    () => (config && drawerReportId != null ? config.reports.find((r) => r.rptReportID === drawerReportId) ?? null : null),
    [config, drawerReportId],
  );

  // ── Mutations ───────────────────────────────────────────────────────────────

  const toggleActive = (r: ReportConfig) => {
    if (r.active) {
      setPauseConfirmId(r.rptReportID); // confirm before pausing
    } else {
      runMutation(() => patchReport(r.rptReportID, { active: true }).then(() => undefined), patchReportErrorMessage);
    }
  };

  const confirmPause = async () => {
    if (pauseConfirmId == null) return;
    const id = pauseConfirmId;
    setPauseConfirmId(null);
    await runMutation(() => patchReport(id, { active: false }).then(() => undefined), patchReportErrorMessage);
  };

  const toggleCc = (r: ReportConfig) => {
    runMutation(() => patchReport(r.rptReportID, { ccRosterSupervisors: !r.ccRosterSupervisors }).then(() => undefined), patchReportErrorMessage);
  };

  const submitAddRecipient = async (reportId: number, input: AddRecipientInput) => {
    await addRecipient(reportId, input);
    await reload();
  };

  const submitEnrollManager = async (reportId: number, employeeID: number, extras?: ManagerExtras) => {
    await enrollManager(reportId, employeeID, extras);
    await reload();
  };

  // ── Per-manager extras (CC list + alternate title + display name + combined email) ──

  const startManagerEdit = (m: ReportOpsManager) => {
    setEditingManagerId(m.rptReportOpsManagerID);
    setMgrCcDraft((m.ccEmails ?? []).join(', '));
    setMgrTitleDraft(m.alternateTitle ?? '');
    setMgrNameDraft(m.displayName ?? '');
    setMgrDistDraft(m.distributionName ?? null);
    setMgrEditErr(null);
  };

  const cancelManagerEdit = () => {
    setEditingManagerId(null);
    setMgrEditErr(null);
  };

  const saveManagerExtras = (m: ReportOpsManager) => {
    const { emails, invalid } = parseCcEmails(mgrCcDraft);
    if (invalid.length) {
      setMgrEditErr(`Not an email address: ${invalid.join(', ')}`);
      return;
    }
    const alternateTitle = mgrTitleDraft.trim() || null;
    const displayName = mgrNameDraft.trim() || null;
    // Already resolved by the picker (normalized, existing spelling reused).
    const distributionName = mgrDistDraft;
    const current = m.ccEmails ?? [];
    const sameCc = current.length === emails.length
      && current.every((e, i) => e.toLowerCase() === emails[i].toLowerCase());
    const sameTitle = (m.alternateTitle ?? null) === alternateTitle;
    const sameName = (m.displayName ?? null) === displayName;
    const sameDist = (m.distributionName ?? null) === distributionName;
    cancelManagerEdit();
    if (sameCc && sameTitle && sameName && sameDist) return;
    // Only the fields that changed go on the wire; an explicit null clears one.
    const patch: ManagerExtras = {};
    if (!sameCc) patch.ccEmails = emails;
    if (!sameTitle) patch.alternateTitle = alternateTitle;
    if (!sameName) patch.displayName = displayName;
    if (!sameDist) patch.distributionName = distributionName;
    runSave(
      `manager:${m.rptReportOpsManagerID}`,
      async () => applyManager(await patchManager(m.rptReportOpsManagerID, patch)),
      patchManagerErrorMessage,
    );
  };

  // ── Trigger-question mutations. All save immediately; no dirty state. ────────

  const submitAddQuestion = async (
    reportId: number,
    input: { timesheetQuestionsID: number; triggerAnswer: string | null },
  ) => {
    await addReportQuestion(reportId, input);
    await reload();
  };

  const saveTrigger = (q: ReportQuestion, triggerAnswer: string | null) => {
    cancelEdit();
    if (q.triggerAnswer === triggerAnswer) return;
    runSave(
      `question:${q.rptReportQuestionID}`,
      async () => applyQuestion(await patchReportQuestion(q.rptReportQuestionID, { triggerAnswer })),
      patchReportQuestionErrorMessage,
    );
  };

  const toggleQuestionChain = (q: ReportQuestion) => {
    runSave(
      `question:${q.rptReportQuestionID}`,
      async () => applyQuestion(
        await patchReportQuestion(q.rptReportQuestionID, { notifySupervisorChain: !q.notifySupervisorChain }),
      ),
      patchReportQuestionErrorMessage,
    );
  };

  const toggleChain = (r: ReportConfig) => {
    const next = !r.notifySupervisorChain;
    runSave(
      `chain:${r.rptReportID}`,
      async () => {
        await patchReport(r.rptReportID, { notifySupervisorChain: next });
        applyReportFlags(r.rptReportID, { notifySupervisorChain: next });
      },
      patchReportErrorMessage,
    );
  };

  const saveStopRole = (r: ReportConfig, roleID: number | null) => {
    runSave(
      `stoprole:${r.rptReportID}`,
      async () => {
        await patchReport(r.rptReportID, { supervisorChainStopRoleID: roleID });
        applyReportFlags(r.rptReportID, { supervisorChainStopRoleID: roleID });
      },
      patchReportErrorMessage,
    );
  };

  const doRemove = () => {
    if (!removeConfirm) return;
    const rc = removeConfirm;
    setRemoveConfirm(null);
    if (rc.kind === 'recipient') {
      runMutation(() => removeRecipient(rc.id), removeRecipientErrorMessage);
    } else if (rc.kind === 'question') {
      runMutation(() => removeReportQuestion(rc.id), removeReportQuestionErrorMessage);
    } else {
      runMutation(() => removeManager(rc.id), removeManagerErrorMessage);
    }
  };

  const openPopover = (kind: 'recipient' | 'manager' | 'question', reportId: number, e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopover({ kind, reportId, anchorRect: rect });
  };

  // ── Loading / error gates ────────────────────────────────────────────────────

  if (loading && !config) {
    return (
      <div className="rptcfg">
        <div className="rc-loading">Loading report configuration…</div>
      </div>
    );
  }

  if (loadError && !config) {
    return (
      <div className="rptcfg">
        <div className="rc-banner warn">
          <AlertTriangle size={19} />
          <div>{loadError}</div>
          <button type="button" className="btn rc-ban-retry" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!config) return <div className="rptcfg" />;

  const settings = config.settings;

  // ── Settings footer (read-only, both views) ──────────────────────────────────
  const settingsFooter = (
    <div className="settings-foot">
      <div className="sf-head">
        <Settings size={15} /><h4>Engine settings</h4>
        <span className="sf-hint"><Lock size={13} />Managed by engineering (RptSetting / SQL)</span>
      </div>
      <div className="sf-grid">
        <div className="sf-item"><div className="sf-lab">Time zone</div><div className="sf-val">{settings.TimeZone ?? '—'}</div></div>
        <div className="sf-item"><div className="sf-lab">State</div><div className="sf-val">{settings.StateAbbr ?? '—'}</div></div>
        <div className="sf-item"><div className="sf-lab">Non-job account code</div><div className="sf-val">{settings.NonJobAccountCodeID ?? '—'}</div></div>
        <div className="sf-item"><div className="sf-lab">Client</div><div className="sf-val">{settings.ClientDisplayName ?? '—'}</div></div>
      </div>
    </div>
  );

  // ── Breadcrumb ────────────────────────────────────────────────────────────────
  const breadcrumb = (
    <div className="crumb">
      <b>Admin</b>
      <ChevronRight className="cx" size={15} />
      {view === 'detail' && report ? (
        <>
          <button type="button" className="backlink" onClick={goList}>Report Configuration</button>
          <ChevronRight className="cx" size={15} />
          <span className="here">{report.name}</span>
        </>
      ) : (
        <span className="here">Report Configuration</span>
      )}
    </div>
  );

  // ── List view ─────────────────────────────────────────────────────────────────
  const renderList = () => {
    const reps = config.reports;
    const active = reps.filter((r) => r.active).length;
    const paused = reps.length - active;
    const totalRcpt = reps.reduce((n, r) => n + activeRecipientCount(r), 0);
    const totalMgr = reps.reduce((n, r) => n + r.opsManagers.length, 0);
    const noopReports = reps.filter(isNoop);

    return (
      <>
        <div className="page-head">
          <span className="ph-ic"><Mail size={23} /></span>
          <div>
            <h1>Report Configuration<span className="clientchip">{settings.ClientDisplayName ?? '—'}</span></h1>
            <div className="ph-sub">
              Manage who receives each automated email report and which managers are enrolled. Schedules and
              deadline rules are engineering-owned and shown read-only. Changes apply to the next scheduled send.
            </div>
          </div>
          <div className="ph-right">
            <div className="periodbox">
              <span className="pb-badge">W{config.payPeriod.weekNumber}</span>
              <span className="pb-range">{formatPeriodRange(config.payPeriod.startDate, config.payPeriod.endDate)}</span>
            </div>
          </div>
        </div>

        <div className="kpis">
          <div className="kpi active"><div className="kl"><CheckCircle2 size={15} />Active</div><div className="kv">{active}</div><div className="ks">reports sending</div></div>
          <div className="kpi paused"><div className="kl"><Pause size={15} />Paused</div><div className="kv">{paused}</div><div className="ks">no emails send</div></div>
          <div className="kpi rcpt"><div className="kl"><Mail size={15} />Recipients</div><div className="kv">{totalRcpt}</div><div className="ks">configured rows</div></div>
          <div className="kpi mgrs"><div className="kl"><Users size={15} />Enrolled managers</div><div className="kv">{totalMgr}</div><div className="ks">across roster reports</div></div>
        </div>

        {noopReports.length > 0 && (
          <div className="rc-banner warn">
            <AlertTriangle size={19} />
            <div>
              {noopReports.every((r) => metaFor(r.reportKey).questionsPanel) ? (
                <>
                  <b>
                    {noopReports.map((r) => r.name).join(', ')} {noopReports.length > 1 ? 'are' : 'is'} active but
                    {noopReports.length > 1 ? ' have' : ' has'} no trigger questions — {noopReports.length > 1 ? 'they' : 'it'} will never fire.
                  </b>
                  {' '}No answer can raise an exception until at least one question is selected.
                </>
              ) : (
                <>
                  <b>{noopReports.length} active report{noopReports.length > 1 ? 's' : ''} {noopReports.length > 1 ? 'send' : 'sends'} nothing.</b>
                  {' '}A roster report with no enrolled managers, or an event-driven report with no trigger questions,
                  is a silent no-op.
                </>
              )}
            </div>
            <button type="button" className="ban-act" onClick={() => openReport(noopReports[0].rptReportID)}>
              <ChevronRight size={15} />
              {metaFor(noopReports[0].reportKey).questionsPanel ? 'Add trigger questions' : `Fix ${noopReports[0].name}`}
            </button>
          </div>
        )}

        <div className="sect">
          <List size={18} /><h2>Reports</h2>
          <span className="sub">{reps.length} configured · driven by RptReport rows</span>
        </div>

        <div className="listwrap">
          {reps.map((r) => {
            const meta = metaFor(r.reportKey);
            const Icon = meta.icon;
            const rc = activeRecipientCount(r);
            const mgrCount = r.opsManagers.length;
            const noop = isNoop(r);
            const unresolved = unresolvedCount(r);
            const mgrWord = meta.managerLabel === 'General managers' ? 'GM' : 'manager';
            return (
              <div
                key={r.rptReportID}
                className={`rcard${r.active ? '' : ' paused'}${noop ? ' warn-noop' : ''}`}
                onClick={() => openReport(r.rptReportID)}
                role="button"
              >
                <span className="rc-ic"><Icon size={22} /></span>
                <div className="rc-main">
                  <div className="rc-name">{r.name}<span className="rc-key">{r.reportKey}</span></div>
                  <div className="rc-meta">
                    {/* The event pill sits in the slot other cards use for a mono
                        schedule, so scheduled and event-driven read side by side. */}
                    {meta.eventDriven
                      ? <span className="m evt"><Zap size={14} />{meta.schedule}</span>
                      : meta.schedule && <span className="m sched"><Clock size={14} />{meta.schedule}</span>}
                    {meta.questionsPanel && (
                      <span className="m"><HelpCircle size={14} /><b>{r.questions.length}</b> trigger question{r.questions.length === 1 ? '' : 's'}</span>
                    )}
                    {!meta.noRecipients && (
                      <span className="m"><Mail size={14} /><b>{rc}</b> recipient{rc === 1 ? '' : 's'}</span>
                    )}
                    {meta.managerPanel && <span className="m"><Users size={14} /><b>{mgrCount}</b> {mgrWord}{mgrCount === 1 ? '' : 's'}</span>}
                    {meta.showDeadlines && <span className="m"><Calendar size={14} />deadline rules</span>}
                    {meta.derivedRecipients && <span className="m"><Navigation size={14} />project-derived</span>}
                    {meta.runnable && <span className="m"><Download size={14} />run on demand</span>}
                    {noop && (
                      <span className="m warn">
                        <AlertTriangle size={14} />
                        {meta.questionsPanel ? 'no trigger questions — never fires' : 'no managers — sends nothing'}
                      </span>
                    )}
                    {meta.questionsPanel && deadQuestionCount(r) > 0 && (
                      <span className="m warn"><AlertTriangle size={14} />{deadQuestionCount(r)} can&apos;t fire</span>
                    )}
                    {unresolved > 0 && <span className="m warn"><AlertTriangle size={14} />{unresolved} unresolved</span>}
                  </div>
                </div>
                <div className="rc-right">
                  <span className={`rpill ${r.active ? 'on' : 'off'}`}><span className="dot" />{r.active ? 'Active' : 'Paused'}</span>
                  <span className="rc-chev"><ChevronRight size={20} /></span>
                </div>
              </div>
            );
          })}
        </div>

        {settingsFooter}
      </>
    );
  };

  // ── Detail panels ─────────────────────────────────────────────────────────────

  const recipientRow = (r: ReportConfig, x: ReportRecipient) => {
    const isEmp = x.employeeID != null;
    const unresolved = isEmp && !x.resolvedEmail;
    const confirming = removeConfirm?.kind === 'recipient' && removeConfirm.id === x.rptReportRecipientID;
    return (
      <div className={`rrow${unresolved ? ' warn' : ''}`} key={x.rptReportRecipientID}>
        <span className={`rtype ${x.recipientType}`}>{x.recipientType}</span>
        {isEmp ? (
          <span className="rav" style={{ background: avatarColor(x.employeeName || '') }}>{initials(x.employeeName || '')}</span>
        ) : (
          <span className="rav lit"><AtSign size={16} /></span>
        )}
        <div className="rmain">
          <div className="rname">
            {isEmp ? x.employeeName : x.email}
            {isEmp ? (
              <span className="rkind emp"><User size={11} />Employee</span>
            ) : (
              <span className="rkind lit"><AtSign size={11} />Literal</span>
            )}
          </div>
          {unresolved ? (
            <div className="rwarn"><AlertTriangle size={13} />Won&apos;t receive — no active admin email</div>
          ) : (
            <div className="remail">{isEmp ? x.resolvedEmail : x.email}</div>
          )}
        </div>
        {confirming ? (
          <span className="rconfirm">
            <span className="rc-q">Remove {isEmp ? 'this recipient' : 'this address'}?</span>
            <button type="button" className="rc-yes" disabled={busy} onClick={doRemove}>Remove</button>
            <span className="rc-sep">·</span>
            <button type="button" className="rc-no" onClick={() => setRemoveConfirm(null)}>Cancel</button>
          </span>
        ) : (
          <button type="button" className="rrm" onClick={() => setRemoveConfirm({ kind: 'recipient', id: x.rptReportRecipientID })}>
            <Trash2 size={14} />Remove
          </button>
        )}
      </div>
    );
  };

  const recipientsPanel = (r: ReportConfig, meta: ReportMeta) => {
    const rows = r.recipients;
    return (
      <div className="panel">
        <div className="p-head">
          <Mail size={16} className="p-ic" /><h3>Recipients</h3>
          <span className="p-cnt">{rows.length}</span>
          <div className="p-right">
            <button type="button" className="addbtn" onClick={(e) => openPopover('recipient', r.rptReportID, e)}><Plus size={15} />Add recipient</button>
          </div>
        </div>
        {meta.derivedRecipients && (
          <div className="derived-note">
            <span className="ei"><Navigation size={18} /></span>
            <div>
              <div className="dn-t">Primary recipients are project-derived</div>
              <div className="dn-b">This report emails one super / foreman per project, resolved from the SupervisorProject tier tables at send time. Those can&apos;t be edited here — only the CC / BCC config rows below apply to every send.</div>
            </div>
          </div>
        )}
        <div className="p-body">
          {rows.length === 0 ? (
            <div className="p-empty noop">
              <span className="ei"><AlertTriangle size={19} /></span>
              <div><span className="e-title">No one will receive this report.</span><br />Add at least one TO recipient so the email has somewhere to go.</div>
            </div>
          ) : (
            RECIPIENT_ORDER.map((t) => {
              const g = rows.filter((x) => x.recipientType === t);
              if (!g.length) return null;
              return (
                <div className="rgroup" key={t}>
                  <div className="rgroup-lab"><span className="glab">{t}</span><span className="gline" /></div>
                  {g.map((x) => recipientRow(r, x))}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const opsPanel = (r: ReportConfig, meta: ReportMeta) => {
    const mgrs = r.opsManagers;
    const zero = r.active && mgrs.length === 0;
    const distGroups = meta.managerExtras ? distributionGroups(mgrs) : [];
    const managerItems = meta.managerExtras
      ? clusterByDistribution(mgrs)
      : mgrs.map((m): ManagerItem => ({ kind: 'one', m }));
    const managerRow = (m: ReportOpsManager) => {
      const confirming = removeConfirm?.kind === 'manager' && removeConfirm.id === m.rptReportOpsManagerID;
      const editing = meta.managerExtras && editingManagerId === m.rptReportOpsManagerID;
      const saving = savingKey === `manager:${m.rptReportOpsManagerID}`;
      const ccList = m.ccEmails ?? [];
      return (
        <div className={`rrow${editing ? ' editing' : ''}`} key={m.rptReportOpsManagerID}>
          <span className="rav" style={{ background: avatarColor(m.name) }}>{initials(m.displayName || m.name)}</span>
          <div className="rmain">
            <div className="rname">
              {m.displayName ? (
                <>{m.displayName}<span className="rname-emp" title="Employee record">{m.name}</span></>
              ) : m.name}
              <span className="rkind emp"><User size={11} />Employee</span>
              {meta.managerExtras && m.alternateTitle && !editing && (
                <span className="mtitle" title="Alternate report title for this manager's email"><Tag size={11} />{m.alternateTitle}</span>
              )}
              {meta.managerExtras && m.distributionName && !editing && (
                <span className="mtitle mdist" title="Shares one combined email with the other managers in this group"><Mail size={11} />Combined: {m.distributionName}</span>
              )}
            </div>
            <div className="mgr-roster">
              <span><b>{m.supervisorCount}</b> supervisors</span>
              <span className="rdot" />
              <span><b>{m.downlineEmployeeCount}</b> downline employees</span>
            </div>
            {meta.managerExtras && !editing && (
              <div className="mgr-extras">
                {ccList.length === 0 ? (
                  <span className="mgr-none">No extra CC · default title</span>
                ) : (
                  <>
                    <span className="mgr-cclab">CC</span>
                    {ccList.map((e) => <span className="ccchip" key={e}><AtSign size={11} />{e}</span>)}
                  </>
                )}
              </div>
            )}
            {editing && (
              <div className="mgr-edit">
                <div className="mgr-field">
                  <label className="fl" htmlFor={`mgr-cc-${m.rptReportOpsManagerID}`}>CC emails</label>
                  <input
                    id={`mgr-cc-${m.rptReportOpsManagerID}`}
                    className={`ti${mgrEditErr ? ' bad' : ''}`}
                    placeholder="name@company.com, other@company.com"
                    autoComplete="off"
                    autoFocus
                    value={mgrCcDraft}
                    onChange={(e) => { setMgrCcDraft(e.target.value); setMgrEditErr(null); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveManagerExtras(m);
                      if (e.key === 'Escape') cancelManagerEdit();
                    }}
                  />
                </div>
                <div className="mgr-field">
                  <label className="fl" htmlFor={`mgr-title-${m.rptReportOpsManagerID}`}>Alternate title</label>
                  <input
                    id={`mgr-title-${m.rptReportOpsManagerID}`}
                    className="ti"
                    placeholder="Default report title"
                    autoComplete="off"
                    maxLength={200}
                    value={mgrTitleDraft}
                    onChange={(e) => setMgrTitleDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveManagerExtras(m);
                      if (e.key === 'Escape') cancelManagerEdit();
                    }}
                  />
                </div>
                <div className="mgr-field">
                  <label className="fl" htmlFor={`mgr-name-${m.rptReportOpsManagerID}`}>Display name</label>
                  <input
                    id={`mgr-name-${m.rptReportOpsManagerID}`}
                    className="ti"
                    placeholder={m.name}
                    autoComplete="off"
                    maxLength={200}
                    value={mgrNameDraft}
                    onChange={(e) => setMgrNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveManagerExtras(m);
                      if (e.key === 'Escape') cancelManagerEdit();
                    }}
                  />
                  <div className="mgr-hint">Shown in the email subject and heading; blank uses the employee record.</div>
                </div>
                <div className="mgr-field">
                  <label className="fl" htmlFor={`mgr-dist-${m.rptReportOpsManagerID}`}>Combine into email</label>
                  <DistributionPicker
                    id={`mgr-dist-${m.rptReportOpsManagerID}`}
                    groups={distGroups}
                    value={mgrDistDraft}
                    onChange={setMgrDistDraft}
                    onKeyDownPassthrough={(e) => {
                      if (e.key === 'Enter') saveManagerExtras(m);
                      if (e.key === 'Escape') cancelManagerEdit();
                    }}
                  />
                  <div className="mgr-hint">Managers with the same name share one email; empty sends their own.</div>
                </div>
                <div className="mgr-edit-foot">
                  {mgrEditErr && <span className="qwarn"><AlertTriangle size={13} />{mgrEditErr}</span>}
                  <span className="mgr-edit-acts">
                    <button type="button" className="tsave" onClick={() => saveManagerExtras(m)}>Save</button>
                    <span className="tsep">·</span>
                    <button type="button" className="tcancel" onClick={cancelManagerEdit}>Cancel</button>
                  </span>
                </div>
              </div>
            )}
          </div>
          {saving ? (
            <span className="qsaving mgr-saving"><span className="rc-spin" />Saving…</span>
          ) : confirming ? (
            <span className="rconfirm">
              <span className="rc-q">Remove this manager?</span>
              <button type="button" className="rc-yes" disabled={busy} onClick={doRemove}>Remove</button>
              <span className="rc-sep">·</span>
              <button type="button" className="rc-no" onClick={() => setRemoveConfirm(null)}>Cancel</button>
            </span>
          ) : editing ? null : (
            <span className="qacts">
              {meta.managerExtras && (
                <button type="button" className="qedit" disabled={busy} onClick={() => startManagerEdit(m)}>
                  <Pencil size={13} />Edit
                </button>
              )}
              <button type="button" className="rrm" onClick={() => setRemoveConfirm({ kind: 'manager', id: m.rptReportOpsManagerID })}>
                <Trash2 size={14} />Remove
              </button>
            </span>
          )}
        </div>
      );
    };

    return (
      <div className={`panel${zero ? ' warn-panel' : ''}`}>
        <div className="p-head">
          <Users size={16} className="p-ic" /><h3>{meta.managerLabel}</h3>
          <span className="p-cnt">{mgrs.length}</span>
          <span className="p-hint">{meta.managerLabel === 'General managers' ? "CC'd on the consolidated send" : distGroups.length > 0 ? 'own email, or one per combined group' : 'each gets their own email'}</span>
          <div className="p-right">
            <button type="button" className="addbtn" onClick={(e) => openPopover('manager', r.rptReportID, e)}><Plus size={15} />Enroll</button>
          </div>
        </div>
        <div className="p-body">
          {mgrs.length === 0 ? (
            <div className={`p-empty${r.active ? ' noop' : ''}`}>
              <span className="ei">{r.active ? <AlertTriangle size={19} /> : <Users size={19} />}</span>
              <div>
                {r.active ? (
                  <><span className="e-title">No managers enrolled — this report sends nothing.</span><br />A roster report with zero managers fires on schedule but produces an empty run. Enroll at least one to start sending.</>
                ) : (
                  <><span className="e-title">No managers enrolled yet.</span><br />Enroll a manager to include their downline in this report.</>
                )}
              </div>
            </div>
          ) : (
            managerItems.map((item) => (item.kind === 'one' ? managerRow(item.m) : (
              <div className="mgr-group" key={`dist:${item.key}`}>
                <div className="mgr-group-lab">
                  <Mail size={13} />
                  <span className="mg-name">{item.name}</span>
                  <span className="mg-cnt">{managerCountLabel(item.members.length)}</span>
                  <span className="mg-hint">one combined email</span>
                </div>
                {item.members.map(managerRow)}
              </div>
            )))
          )}
        </div>
        {meta.ccToggle && (
          <div className="ccstrip">
            <div className="cc-txt">
              <div className="cc-t">CC roster supervisors</div>
              <div className="cc-h">CC each enrolled manager&apos;s own supervisors on the email they receive.</div>
            </div>
            <Toggle on={r.ccRosterSupervisors} small busy={busy} ariaLabel="CC roster supervisors" onClick={() => toggleCc(r)} />
          </div>
        )}
      </div>
    );
  };

  // ── Trigger questions (TAS-123) ─────────────────────────────────────────────

  /**
   * The three trigger variants, distinguished by colour and shape rather than only
   * by text. The filled "Yes" variant is the only filled chip on the page, so an
   * inverted configuration — where the exception fires on Yes rather than No —
   * reads as inverted at a glance.
   */
  const triggerChip = (q: ReportQuestion) => {
    if (q.triggerAnswer == null) {
      return <span className="tchip any"><Asterisk size={12} />fires on <b>any answer</b></span>;
    }
    if (/^y/i.test(q.triggerAnswer)) {
      return (
        <span className="tchip yes">
          <AlertTriangle size={12} />fires on <b>{q.triggerAnswer}</b>
          <span className="tsub">INVERTED</span>
        </span>
      );
    }
    return <span className="tchip no"><XCircle size={12} />fires on <b>{q.triggerAnswer}</b></span>;
  };

  const answerTypeChip = (answerType: number | null) => {
    const spec = ANSWER_TYPE_META[answerType ?? -1];
    // A selection whose question is gone has no answer type. Say so rather than
    // rendering nothing, so the row doesn't look merely unfinished.
    if (!spec) return <span className="atchip"><AlertTriangle size={11} />Unknown type</span>;
    const Ic = spec.icon;
    const isWaiver = answerType === TimesheetAnswerType.MealConditionalWaiver;
    return (
      <span
        className={`atchip${isWaiver ? ' waiver' : ''}`}
        {...(isWaiver ? { 'data-tip': 'Meal-waiver answers are stored as "No" or "Yes - 07/01, 07/02". A Yes trigger matches both — the bare Yes and the version carrying waived dates.' } : {})}
      >
        <Ic size={11} />{spec.label}
      </span>
    );
  };

  /** What the chain flag resolves to for this question, said next to the trigger. */
  const chainResult = (r: ReportConfig, q: ReportQuestion) => {
    if (!q.notifySupervisorChain) return null;
    if (!r.notifySupervisorChain) {
      return <span className="chainres off"><ChevronsUp size={11} />chain off</span>;
    }
    const stop = roles.find((x) => x.roleID === r.supervisorChainStopRoleID);
    return (
      <span className="chainres">
        <ChevronsUp size={11} />
        {stop ? <>chain up to <b>{stop.name}</b></> : <>chain to the <b>top</b></>}
      </span>
    );
  };

  const questionRow = (r: ReportConfig, q: ReportQuestion) => {
    const confirming = removeConfirm?.kind === 'question' && removeConfirm.id === q.rptReportQuestionID;
    const editing = editingQuestionId === q.rptReportQuestionID;
    const missing = q.question == null;
    const dead = !missing && !q.questionActive;
    const child = q.parentQuestionID != null;
    const parent = child ? r.questions.find((x) => x.timesheetQuestionsID === q.parentQuestionID) : null;
    const hasChildren = questionOptions.some((o) => o.parentQuestionID === q.timesheetQuestionsID);
    // Date and Comments can only ever be "any answer" — nothing to edit.
    const editable = q.answerType != null
      && q.answerType !== TimesheetAnswerType.Date
      && q.answerType !== TimesheetAnswerType.Comments;
    const isComment = q.answerType === TimesheetAnswerType.Comments;

    return (
      <div
        className={`qrow${child && parent ? ' child' : ''}${dead ? ' off' : ''}${missing ? ' gone' : ''}`}
        key={q.rptReportQuestionID}
      >
        {child && parent && <span className="qchild-ic"><CornerDownRight size={14} /></span>}
        <div className="qmain">
          {missing ? (
            <>
              <div className="qtext gone-t">Question no longer exists (id {q.timesheetQuestionsID})</div>
              <div className="qchips">{answerTypeChip(q.answerType)}</div>
              <div className="qwarn">
                <AlertTriangle size={12} />
                Question no longer exists (id {q.timesheetQuestionsID}). Nothing can match it — remove this row.
              </div>
            </>
          ) : (
            <>
              {child && !parent && (
                <div className="qbread">Child of <b>{parentLabel(q)}</b></div>
              )}
              <div className="qtext">
                {q.order != null && <span className="qord">Q{q.order}</span>}
                {q.question}
              </div>
              {esOpen.has(q.rptReportQuestionID) && q.questionEs && (
                <div className="qes">{q.questionEs}</div>
              )}

              {editing ? (
                // Pick, then Save — a mis-click on the segmented control is
                // recoverable, which it would not be if each click saved.
                <div className="tseg-wrap">
                  <div className="tseg">
                    {(['No', 'Yes', null] as (string | null)[]).map((v) => (
                      <button
                        key={v ?? 'any'}
                        type="button"
                        className={`tseg-b${pendingTrigger === v ? ' on' : ''}${v === 'No' ? ' no' : ''}${v === 'Yes' ? ' yes' : ''}`}
                        disabled={busy}
                        onClick={() => setPendingTrigger(v)}
                      >
                        {v ?? 'Any answer'}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="tsave" disabled={busy} onClick={() => saveTrigger(q, pendingTrigger)}>Save</button>
                  <span className="tsep">·</span>
                  <button type="button" className="tcancel" onClick={cancelEdit}>Cancel</button>
                  <div className="qbilingual">Matches both English and Spanish answers (Yes / Sí).</div>
                </div>
              ) : (
                <div className="qchips">
                  {triggerChip(q)}
                  {answerTypeChip(q.answerType)}
                  {chainResult(r, q)}
                  {q.questionEs && (
                    <button type="button" className="esbtn" onClick={() => toggleEs(q.rptReportQuestionID)}>ES</button>
                  )}
                </div>
              )}

              {dead && (
                <div className="qwarn"><AlertTriangle size={12} />This question is switched off — it can never fire.</div>
              )}
              {child && parent && (
                <div className="qnote">
                  Only asked when &ldquo;{parent.question}&rdquo; is answered <b>Yes</b>. Selected here, it also raises
                  an exception of its own.
                </div>
              )}
              {!child && hasChildren && (
                <div className="qnote">Child answers are included automatically when this fires — no need to select them.</div>
              )}
              {q.notifySupervisorChain && isComment && r.notifySupervisorChain && (
                <div className="qwarn amber">
                  <Eye size={12} />
                  Free text — whatever the employee writes here is emailed to their own supervisor and everyone above them.
                </div>
              )}
            </>
          )}
        </div>

        <div className="qacts">
          {savingKey === `question:${q.rptReportQuestionID}` && (
            <span className="qsaving"><span className="rc-spin" />Saving…</span>
          )}
          {!missing && (
            <label className={`chainbtn${r.notifySupervisorChain ? '' : ' dim'}`}>
              <input
                type="checkbox"
                checked={q.notifySupervisorChain}
                disabled={busy}
                onChange={() => toggleQuestionChain(q)}
              />
              Notify chain
            </label>
          )}
          {editable && !editing && !confirming && (
            <button type="button" className="qedit" onClick={() => beginEdit(q)}>
              <Pencil size={14} />Edit
            </button>
          )}
          {confirming ? (
            <span className="rconfirm">
              <span className="rc-q">Remove this question?</span>
              <button type="button" className="rc-yes" disabled={busy} onClick={doRemove}>Remove</button>
              <span className="rc-sep">·</span>
              <button type="button" className="rc-no" onClick={() => setRemoveConfirm(null)}>Cancel</button>
            </span>
          ) : (
            <button type="button" className="rrm" onClick={() => setRemoveConfirm({ kind: 'question', id: q.rptReportQuestionID })}>
              <Trash2 size={14} />Remove
            </button>
          )}
        </div>
      </div>
    );
  };

  /**
   * Rows in the order the employee sees the questions, with a selected child
   * nested directly under its selected parent.
   */
  const orderedQuestions = (r: ReportConfig): ReportQuestion[] => {
    const byOrder = [...r.questions].sort((a, b) => {
      const ao = a.order ?? Number.MAX_SAFE_INTEGER;
      const bo = b.order ?? Number.MAX_SAFE_INTEGER;
      return ao !== bo ? ao - bo : a.timesheetQuestionsID - b.timesheetQuestionsID;
    });
    const selectedIds = new Set(byOrder.map((q) => q.timesheetQuestionsID));
    const out: ReportQuestion[] = [];
    for (const q of byOrder) {
      // A child whose parent is also selected is emitted under it, not at root.
      if (q.parentQuestionID != null && selectedIds.has(q.parentQuestionID)) continue;
      out.push(q);
      for (const c of byOrder) {
        if (c.parentQuestionID === q.timesheetQuestionsID) out.push(c);
      }
    }
    return out;
  };

  const chainNote = (r: ReportConfig) => {
    const flagged = r.questions.filter((q) => q.notifySupervisorChain).length;

    if (!r.notifySupervisorChain) {
      if (flagged === 0) return null;
      return (
        <div className="chainnote muted">
          <b>{flagged} question{flagged === 1 ? ' is' : 's are'} flagged to notify the chain.</b>{' '}
          Those flags are kept and do nothing while this is off.
        </div>
      );
    }

    if (flagged === 0) {
      return (
        <div className="chainnote warn">
          <AlertTriangle size={14} />
          <div>
            <b>No questions are flagged, so nothing escalates.</b> The chain is on but no trigger question is set to
            use it — alerts go only to the recipients above.
          </div>
        </div>
      );
    }

    const stop = roles.find((x) => x.roleID === r.supervisorChainStopRoleID);
    if (!stop) {
      return (
        <div className="chainnote info">
          <Info size={14} />
          <div>
            <b>No stop role set — the chain is walked to the top.</b> Every supervisor above the employee is CC&apos;d,
            however many hops that is. This is deliberate, not unfinished.
          </div>
        </div>
      );
    }

    // A non-supervisor role can never match a supervisor in the chain, so the walk
    // would run to the top regardless — which is not what picking a stop implies.
    if (!stop.supervisor) {
      return (
        <div className="chainnote warn">
          <AlertTriangle size={14} />
          <div>
            <b>{stop.name} is held by {stop.employeeCount} {stop.employeeCount === 1 ? 'person' : 'people'}.</b>{' '}
            A widely-held role usually stops the walk at the first supervisor — pick the role that only the people you
            want to reach hold.
          </div>
        </div>
      );
    }
    return null;
  };

  const triggerPanel = (r: ReportConfig) => {
    const rows = orderedQuestions(r);
    const zero = r.questions.length === 0;
    const flagged = r.questions.filter((q) => q.notifySupervisorChain).length;

    return (
      <div className={`panel${zero && r.active ? ' warn-panel' : ''}`}>
        <div className="p-head">
          <HelpCircle size={16} className="p-ic" /><h3>Trigger questions</h3>
          <span className="p-cnt">{r.questions.length}</span>
          <span className="p-hint">any of these fires one email</span>
          <div className="p-right">
            {savingKey && <span className="p-saving"><span className="rc-spin" />Saving…</span>}
            <button type="button" className="addbtn" onClick={(e) => openPopover('question', r.rptReportID, e)}>
              <Plus size={15} />Add question
            </button>
          </div>
        </div>

        <div className="p-body">
          {zero ? (
            <div className={`p-empty${r.active ? ' noop' : ''}`}>
              <span className="ei">{r.active ? <AlertTriangle size={19} /> : <HelpCircle size={19} />}</span>
              <div>
                {r.active ? (
                  <><span className="e-title">No trigger questions selected — this report will never fire.</span><br />Add at least one question so submissions can raise an exception.</>
                ) : (
                  <><span className="e-title">No trigger questions selected yet.</span><br />Choose the questions whose answers should raise an exception.</>
                )}
              </div>
            </div>
          ) : (
            rows.map((q) => questionRow(r, q))
          )}
        </div>

        <div className="ccstrip chainstrip">
          <span className="chain-ic"><ChevronsUp size={16} /></span>
          <div className="cc-txt">
            <div className="cc-t">Notify the supervisor chain</div>
            <div className="cc-h">
              CC the employee&apos;s supervisor and everyone above them, up to and including the stop role. Deduped
              against the recipients above — nobody is mailed twice, and BCC is never promoted to CC.
            </div>
          </div>
          {savingKey === `chain:${r.rptReportID}` && (
            <span className="qsaving"><span className="rc-spin" />Saving…</span>
          )}
          <Toggle
            on={r.notifySupervisorChain}
            small
            busy={busy}
            ariaLabel="Notify the supervisor chain"
            onClick={() => toggleChain(r)}
          />
        </div>

        {r.notifySupervisorChain && (
          <div className="chainrow">
            <span className="chain-lab">Stop at role</span>
            <select
              className="rolesel"
              value={r.supervisorChainStopRoleID ?? ''}
              disabled={busy}
              onChange={(e) => saveStopRole(r, e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">Not set — walk to the top</option>
              {roles.map((role) => (
                <option key={role.roleID} value={role.roleID}>
                  {role.name} · {role.employeeCount} {role.employeeCount === 1 ? 'person' : 'people'}
                </option>
              ))}
            </select>
            {savingKey === `stoprole:${r.rptReportID}` && (
              <span className="qsaving"><span className="rc-spin" />Saving…</span>
            )}
            <span className="chain-cnt">{flagged} of {r.questions.length} questions flagged</span>
          </div>
        )}

        {chainNote(r)}
      </div>
    );
  };

  /**
   * On-demand run. The API proxies the ReportEngine function, so this is the same query
   * the scheduled email uses — no second implementation to drift. Downloads only; nothing
   * is emailed and no configuration changes, so there is no reload() afterwards.
   */
  const runPanel = (r: ReportConfig) => {
    const selectedPeriod = recentPeriods.find((p) => p.payPeriodID === runPayPeriodID) ?? null;
    // "Pick a period" only exists once the API sends the list — an older build keeps
    // the two relative scopes rather than offering an empty dropdown.
    const modes: { mode: RunMode; label: string }[] = [
      { mode: 'prior', label: 'Last week' },
      { mode: 'current', label: 'This week' },
      ...(recentPeriods.length > 0
        ? [{ mode: 'period' as RunMode, label: 'Pick a period' }]
        : []),
    ];

    const doRun = async () => {
      let target: ReportRunTarget;
      if (runMode === 'period') {
        if (!selectedPeriod) return;
        target = { payPeriodID: selectedPeriod.payPeriodID };
      } else {
        target = { scope: runMode };
      }
      setRunning(true);
      try {
        await downloadReportExcel(r.reportKey, target);
      } catch (e) {
        createPopup('Couldn\'t build the report', await downloadReportExcelErrorMessage(e));
      } finally {
        setRunning(false);
      }
    };

    const hint =
      runMode === 'current'
        ? `The period in progress (W${config.payPeriod.weekNumber}) — still filling in.`
        : runMode === 'prior'
          ? 'The period that just closed — what Monday’s email covers.'
          : selectedPeriod
            ? `Week ${selectedPeriod.weekNumber} · ${formatPeriodRange(selectedPeriod.startDate, selectedPeriod.endDate)}`
            : 'Choose a pay period to run.';

    return (
      <div className="panel">
        <div className="p-head">
          <Download size={16} className="p-ic" /><h3>Run now</h3>
        </div>
        <div className="p-note">
          Builds the workbook and downloads it. Nothing is emailed and no recipient sees it
          {!r.active && <> — this works even while the report is paused</>}.
        </div>
        <div className="p-body">
          <div className="runrow">
            <div className="seg">
              {modes.map((m) => (
                <button
                  key={m.mode}
                  type="button"
                  className={m.mode === runMode ? 'on' : ''}
                  disabled={running}
                  onClick={() => setRunMode(m.mode)}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {runMode === 'period' && (
              <select
                className="run-period"
                value={runPayPeriodID ?? ''}
                disabled={running}
                onChange={(e) => setRunPayPeriodID(Number(e.target.value))}
              >
                {recentPeriods.map((p) => (
                  <option key={p.payPeriodID} value={p.payPeriodID}>
                    Week {p.weekNumber} · {formatPeriodRange(p.startDate, p.endDate)}
                  </option>
                ))}
              </select>
            )}
            <span className="run-hint">{hint}</span>
            <button
              type="button"
              className="whobtn"
              disabled={running || (runMode === 'period' && !selectedPeriod)}
              onClick={doRun}
            >
              <Download size={15} />{running ? 'Building…' : 'Download Excel'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const deadlinesPanel = () => (
    <div className="panel">
      <div className="p-head">
        <Calendar size={16} className="p-ic" /><h3>Deadline rules</h3>
        <div className="p-right"><span className="ro-badge"><Lock size={12} />Read-only</span></div>
      </div>
      <div className="p-note">Deadlines are defined in RptDeadlineRule as an offset from pay-period end plus a local time. Managed by engineering.</div>
      <div className="p-body">
        {config.deadlineRules.map((rule) => {
          const res = resolveDeadline(rule, config.payPeriod.endDate, settings.StateAbbr);
          const label = deadlineLabel(rule.ruleKey);
          return (
            <div className="drow" key={rule.ruleKey}>
              <div className="dkey"><div className="dk-lab">{rule.ruleKey}</div><div className="dk-name">{label}</div></div>
              <div className="drule">{label} deadline is <b>{res.offsetText}</b> at <b>{res.time12}{res.zone}</b>.</div>
              <div className="dresolved"><span className="lab">This period (W{config.payPeriod.weekNumber})</span>{res.display}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Detail view ───────────────────────────────────────────────────────────────
  const renderDetail = () => {
    if (!report) { return renderList(); }
    const r = report;
    const meta = metaFor(r.reportKey);
    const Icon = meta.icon;
    return (
      <>
        <div className="detail-head">
          <div className="dh-top">
            <span className="dh-ic"><Icon size={23} /></span>
            <div className="dh-title">
              <h1>{r.name}<span className="rc-key">{r.reportKey}</span></h1>
              <div className="dh-meta">
                {meta.eventDriven ? (
                  <>
                    <span className="m evt"><Zap size={14} />{meta.schedule}</span>
                    <span className="m"><Info size={14} />Event-driven — there is no schedule and no timer</span>
                  </>
                ) : (
                  <>
                    {meta.schedule && <span className="m sched"><Clock size={14} />{meta.schedule}</span>}
                    {meta.schedule !== ON_DEMAND_SCHEDULE && (
                      <span className="m"><Calendar size={14} />Schedule set in Function App timers — not editable</span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="dh-right">
              <div className="actrow">
                <span className="actlab">{r.active ? 'Active' : 'Paused'}</span>
                <Toggle on={r.active} busy={busy} ariaLabel="Active" onClick={() => toggleActive(r)} />
              </div>
              {!meta.noRecipients && (
                <button type="button" className="whobtn" onClick={() => setDrawerReportId(r.rptReportID)}><Eye size={15} />Who will get this?</button>
              )}
            </div>
          </div>
          {meta.desc && <div className="dh-desc">{meta.desc}</div>}
          {!r.active && (
            meta.questionsPanel ? (
              // Pausing this report is not the same as pausing a scheduled one:
              // exceptions keep being recorded, only the email stops. Saying so
              // plainly is the difference between "paused" and "broken".
              <div className="paused-note keeps">
                <Pause size={16} />
                <div>
                  <b>Paused — no emails send.</b> Exceptions are still recorded and still appear on the
                  Timesheet Exceptions page, so nothing is lost while this is off.
                </div>
              </div>
            ) : (
              <div className="paused-note">
                <Pause size={16} />
                <div><b>Paused — no emails send.</b> The timer keeps firing on schedule but the report no-ops until reactivated. Nothing is deleted.</div>
              </div>
            )
          )}
        </div>

        {meta.runnable && runPanel(r)}
        {/* Above Recipients deliberately: "when does this fire" before "who hears
            about it", and it is the panel that can silently make the report pointless. */}
        {meta.questionsPanel && triggerPanel(r)}
        {!meta.noRecipients && recipientsPanel(r, meta)}
        {meta.managerPanel && opsPanel(r, meta)}
        {meta.showDeadlines && config.deadlineRules.length > 0 && deadlinesPanel()}

        {settingsFooter}
      </>
    );
  };

  const activePopoverReport = popover ? config.reports.find((r) => r.rptReportID === popover.reportId) ?? null : null;
  const pauseReport = pauseConfirmId != null ? config.reports.find((r) => r.rptReportID === pauseConfirmId) ?? null : null;

  return (
    <div className="rptcfg">
      {breadcrumb}
      {view === 'detail' ? renderDetail() : renderList()}

      {popover && activePopoverReport && popover.kind === 'recipient' && (
        <AddRecipientPopover
          report={activePopoverReport}
          anchorRect={popover.anchorRect}
          onClose={() => setPopover(null)}
          onSubmit={(input) => submitAddRecipient(activePopoverReport.rptReportID, input)}
        />
      )}
      {popover && activePopoverReport && popover.kind === 'manager' && (
        <EnrollManagerPopover
          report={activePopoverReport}
          managerLabel={metaFor(activePopoverReport.reportKey).managerLabel}
          managerExtras={metaFor(activePopoverReport.reportKey).managerExtras}
          anchorRect={popover.anchorRect}
          onClose={() => setPopover(null)}
          onSubmit={(employeeID, extras) => submitEnrollManager(activePopoverReport.rptReportID, employeeID, extras)}
        />
      )}

      {popover && activePopoverReport && popover.kind === 'question' && (
        <AddQuestionPopover
          report={activePopoverReport}
          options={questionOptions}
          anchorRect={popover.anchorRect}
          onClose={() => setPopover(null)}
          onSubmit={(input) => submitAddQuestion(activePopoverReport.rptReportID, input)}
        />
      )}

      {drawerReport && (
        <ResolvedDrawer report={drawerReport} config={config} onClose={() => setDrawerReportId(null)} />
      )}

      {pauseReport && (
        <ConfirmModal
          variant="success"
          icon={<Pause size={22} />}
          title="Pause this report?"
          detail={(
            <span>
              <strong>{pauseReport.name}</strong> will stop sending. The timer keeps firing on schedule but the report
              no-ops — no emails go out — until you reactivate it. Nothing is deleted.
            </span>
          )}
          confirmLabel="Pause report"
          loading={busy}
          onConfirm={confirmPause}
          onClose={() => setPauseConfirmId(null)}
        />
      )}
    </div>
  );
};

export default ReportsConfigPage;
